import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "../api/client";
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  Field,
  Notice,
  Tabs,
  useUnsavedChanges,
} from "../components/ui";
import { absenceInContext } from "./planning-context";
import { offerFollowUpState } from "./offer-status";
import { offerPipeline } from "./offer-pipeline";
import {
  OFFER_STATUSES,
  canMoveOffer,
  offerStatus,
  offerStatusPatch,
} from "./offer-workflow";
import { downloadPlanningEntryIcs } from "./planning-ics";
import {
  movePlanningPeriod,
  planningPeriod,
  planningPeriodLabel,
  dashboardWeekLabel,
  fiscalYearLabel,
  isoWeekDate,
  isoWeekFor,
  isoWeeksInYear,
  fiscalYearFor,
  fiscalYearStartFor,
  monthDate,
  planningYearFor,
  planningYearLabel,
} from "./planning-period";
import { employeeColor, projectColor } from "./planning-colors";
import {
  calendarDays,
  calendarRows,
  monthCalendarWeeks,
  fiscalWeeks,
  planningMoveBody,
} from "./planning-calendar";
import {
  activeMandates,
  criticalDeadlines,
  dashboardWeek,
  entriesForDashboardPeriod,
  filterTeamCapacity,
  mandatesWithoutPlanning,
  personalDashboardPeriods,
  teamAttention,
  upcomingExternalAppointments,
} from "./dashboard-data";
import {
  decorateProjects,
  DISPOSITION_LABELS,
  filterProjects,
  projectFilterFromSearch,
  projectFilterSearch,
  projectSnapshots,
  sortProjects,
} from "./project-portfolio";
import { ConfirmationView } from "./ConfirmationView";
import { CONFIRMATION_STATUSES, groupConfirmations } from "./confirmation-domain";
import { CAPACITY_LEGEND, capacityStatus } from "./capacity-status";
import {
  authorizedPlanningMandates,
  PlanningMandateSidebar,
} from "./PlanningMandateSidebar";
import {
  planningEntriesPath,
  planningFilters,
  planningFilterSearch,
} from "./planning-filters";
import { MandateImportDialog } from "./MandateImportDialog";
import { downloadMandateTemplate } from "./mandate-import-file";
import { ReportDialog } from "./ReportDialog";
import { ProjectDetailDialog } from "./ProjectDetailDialog";
import { ProjectConfirmations } from "./ProjectConfirmations";
import { ProjectPlanningPanel } from "./ProjectPlanningPanel";
import { ProjectForm } from "./ProjectForm";
const newIdempotencyKey = (prefix) =>
  `${prefix}-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`}`;

const projectColumnValue = (column, row) =>
  String(column.value ? column.value(row) : (row[column.key] ?? ""));
export function ProjectPortfolioTable({
  columns,
  rows,
  onOpen,
  sort,
  direction,
  onSort,
}) {
  const [filters, setFilters] = useState({});
  const [menu, setMenu] = useState(null);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(new Set());
  const values = (column) =>
    [...new Set(rows.map((row) => projectColumnValue(column, row)))].sort(
      (a, b) => a.localeCompare(b, "de-CH", { numeric: true }),
    );
  const filtered = rows.filter((row) =>
    columns.every(
      (column) =>
        !filters[column.key] ||
        filters[column.key].has(projectColumnValue(column, row)),
    ),
  );
  const openMenu = (column) => {
    const unique = values(column);
    setDraft(new Set(filters[column.key] || unique));
    setSearch("");
    setMenu(menu === column.key ? null : column.key);
  };
  const activeColumn = columns.find((column) => column.key === menu);
  const visibleValues = activeColumn
    ? values(activeColumn).filter((value) =>
        (value || "(leer)")
          .toLocaleLowerCase("de-CH")
          .includes(search.toLocaleLowerCase("de-CH")),
      )
    : [];
  const activate = (event, row) => {
    if (event.type === "click" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(row);
    }
  };
  return (
    <div className="project-table-area">
      {Object.keys(filters).length > 0 && (
        <Button
          variant="secondary"
          className="project-column-reset"
          onClick={() => {
            setFilters({});
            setMenu(null);
          }}
        >
          Spaltenfilter zurücksetzen
        </Button>
      )}
      <div
        className="project-table-wrap table-wrap"
        role="region"
        aria-label="Projekttabelle"
        tabIndex="0"
      >
        <table className="project-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>
                  <button
                    className="project-sort"
                    onClick={() => onSort(column.key)}
                    aria-label={`Nach ${column.label} sortieren`}
                    aria-pressed={sort === column.key}
                  >
                    {column.label}
                    {sort === column.key
                      ? direction === "asc"
                        ? " ↑"
                        : " ↓"
                      : ""}
                  </button>
                  <button
                    className={`project-column-filter ${filters[column.key] ? "active" : ""}`}
                    title="Filtern"
                    aria-label={`${column.label} filtern`}
                    aria-expanded={menu === column.key}
                    onClick={() => openMenu(column)}
                  >
                    ▾
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr
                key={row.id}
                role="link"
                tabIndex="0"
                aria-label={`${row.name} öffnen`}
                onClick={(event) => activate(event, row)}
                onKeyDown={(event) => activate(event, row)}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    title={
                      column.key === "name" ? "Klicken für Details" : undefined
                    }
                  >
                    {column.render
                      ? column.render(row)
                      : projectColumnValue(column, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="project-table-hint">
        {filtered.length} von {rows.length} Zeilen (nach Spaltenfiltern) · Zeile
        anklicken für Projekt-Details. Tipp: „Als Excel exportieren“ liefert
        dieselben Spalten zur Weiterbearbeitung.
      </p>
      {activeColumn && (
        <div
          className="project-column-popover"
          role="dialog"
          aria-label={`${activeColumn.label} filtern`}
        >
          <input
            autoFocus
            aria-label="Filterwerte durchsuchen"
            placeholder="Suchen"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="project-column-values">
            <label>
              <input
                type="checkbox"
                checked={
                  visibleValues.length > 0 &&
                  visibleValues.every((value) => draft.has(value))
                }
                onChange={(event) =>
                  setDraft((current) => {
                    const next = new Set(current);
                    visibleValues.forEach((value) =>
                      event.target.checked
                        ? next.add(value)
                        : next.delete(value),
                    );
                    return next;
                  })
                }
              />
              <b>(Alle auswählen)</b>
            </label>
            {visibleValues.map((value) => (
              <label key={value || "empty"}>
                <input
                  type="checkbox"
                  checked={draft.has(value)}
                  onChange={(event) =>
                    setDraft((current) => {
                      const next = new Set(current);
                      event.target.checked
                        ? next.add(value)
                        : next.delete(value);
                      return next;
                    })
                  }
                />
                {value || <i>(leer)</i>}
              </label>
            ))}
          </div>
          <div className="project-column-actions">
            <Button
              onClick={() => {
                const unique = values(activeColumn);
                setFilters((current) => {
                  const next = { ...current };
                  if (
                    draft.size === unique.length &&
                    unique.every((value) => draft.has(value))
                  )
                    delete next[activeColumn.key];
                  else next[activeColumn.key] = new Set(draft);
                  return next;
                });
                setMenu(null);
              }}
            >
              OK
            </Button>
            <Button variant="secondary" onClick={() => setMenu(null)}>
              Abbrechen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export const dateCH = (value) => {
  if (!value) return "–";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : "–";
};
const mandateDotIndex = (mandateId = "") => {
  let hash = 0;
  for (const character of String(mandateId))
    hash = (hash * 31 + character.charCodeAt(0)) % 8;
  return hash;
};
export const capacityLegend = CAPACITY_LEGEND;
const isoWeekNumber = (value) => {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const year = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - year) / 86400000 + 1) / 7);
};
const money = (value) =>
  new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    maximumFractionDigits: 0,
  }).format(value || 0);
const projectBudget = (value) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
export const dispositionPresentation = (value) =>
  ({
    flex: "Flexibel",
    fx: "Fix (veraltet)",
    "fx-bü": "Fix – Inhouse",
    "fx-ext": "Fix – extern",
    nfx: "Nicht fix",
    res: "Reserviert",
  })[value] ||
  value ||
  "nicht fix";
const PageHeader = ({ title, meta }) => (
  <header className="page-header">
    <h1>{title}</h1>
    <span>{meta}</span>
  </header>
);
const permissionLabel = (permission) =>
  ({
    administrator: "Administrator",
    projektleiter: "Projektleiter",
    mitarbeiter: "Mitarbeiter",
  })[String(permission || "").toLocaleLowerCase("de-CH")] || permission;
function useData(path) {
  const [state, setState] = useState({ status: "loading", data: null });
  const reload = useCallback(() => {
    if (!path) {
      setState({ status: "success", data: { items: [] } });
      return;
    }
    setState((s) => ({ ...s, status: "loading" }));
    api(path)
      .then((data) =>
        setState({ status: "success", data, receivedAt: new Date() }),
      )
      .catch((error) =>
        setState((s) => ({
          status: "error",
          error,
          data: s.data,
          receivedAt: s.receivedAt,
        })),
      );
  }, [path]);
  useEffect(reload, [reload]);
  return { ...state, reload };
}
function LoadState({ state, children, keepPreviousData = false }) {
  if (keepPreviousData)
    return <>
      {state.status !== "success" && <LoadState state={state} />}
      {state.data != null && !(state.status === "error" && state.error?.status === 403) && children}
    </>;
  if (state.status === "loading")
    return <Notice>Daten werden geladen …</Notice>;
  if (state.status === "error")
    return (
      <Notice tone="error">
        <strong>
          {state.error?.status === 403
            ? "Kein Zugriff."
            : "Daten konnten nicht geladen werden."}
        </strong>{" "}
        {state.error?.message || String(state.error)}{" "}
        {state.error?.status !== 403 && (
          <Button variant="secondary" onClick={state.reload}>
            Erneut versuchen
          </Button>
        )}
      </Notice>
    );
  return children;
}

export function Dashboard({ user, settings, navigate }) {
  const params = new URLSearchParams(window.location.search),
    requested = params.get("von"),
    initial =
      requested && /^\d{4}-\d{2}-\d{2}$/.test(requested)
        ? requested
        : new Date().toISOString().slice(0, 10);
  const period = dashboardWeek(initial),
    personalQuery = user.berechtigungsstufe === "consultant" && user.mitarbeiterId ? `&mitarbeiterId=${encodeURIComponent(user.mitarbeiterId)}` : "",
    cap = useData(`/capacity?from=${period.from}&weeks=2${personalQuery}`),
    plan = useData("/planning-entries?limit=1000"),
    mandates = useData("/mandates?limit=1000"),
    milestones = useData("/milestones?limit=1000"),
    confirmations = useData("/confirmations?limit=1000"),
    confirmationStatuses = useData(
      "/reference-data/bestaetigungsstatus?limit=100",
    ),
    offers = useData("/offers?limit=1000"),
    offerFollowUps = useData("/offer-follow-ups?limit=1000"),
    employees = useData("/employees?limit=1000"),
    personnelCategories = useData(
      "/reference-data/personalkategorien?limit=100",
    ),
    dispositions = useData("/reference-data/dispositionsstatus?limit=100");
  const open = activeMandates(mandates.data?.items || []),
    employee = (cap.data || []).find(
      (item) => item.mitarbeiterId === user.mitarbeiterId,
    ),
    sections = personalDashboardPeriods(period.anchor),
    personnelCategoryNames = new Map(
      (personnelCategories.data?.items || [])
        .filter((category) => category.aktiv !== false)
        .map((category) => [category.id, category.name]),
    ),
    personnelCategoryName =
      personnelCategoryNames.get(employee?.personalkategorieId) || "Teammitglied",
    dispositionById = new Map(
      (dispositions.data?.items || []).map((disposition) => [
        disposition.id,
        {
          code: disposition.code || disposition.name,
          name: disposition.beschreibung || disposition.name,
        },
      ]),
    );
  const unplanned = mandatesWithoutPlanning(
      mandates.data?.items || [],
      plan.data?.items || [],
    ),
    deadlines = criticalDeadlines(
      milestones.data?.items || [],
      mandates.data?.items || [],
    ),
    externalAppointments = upcomingExternalAppointments(
      plan.data?.items || [],
      mandates.data?.items || [],
      cap.data || [],
    );
  const personalSources = [
      cap,
      plan,
      mandates,
      personnelCategories,
      dispositions,
    ],
    teamSources = [cap, plan, mandates, milestones, confirmations, confirmationStatuses],
    sources = teamSources;
  const personalFailures = personalSources.filter(
      (state) => state.status === "error",
    ),
    teamFailures = teamSources.filter((state) => state.status === "error"),
    loading = sources.some((state) => state.status === "loading");
  const personalForbidden = personalFailures.some(
      (state) => state.error?.status === 403,
    ),
    teamForbidden = teamFailures.some((state) => state.error?.status === 403),
    staleCapacity =
      cap.status === "success" &&
      cap.data?.some((item) => item.wochen?.[0]?.von !== period.from);
  const retry = (list) =>
    list.forEach((source) => source.status === "error" && source.reload());
  const failureNotice = (list) =>
    list.length > 0 && (
      <Notice tone="error">
        Einige Daten dieses Abschnitts konnten nicht geladen werden:{" "}
        {list.map((x) => x.error?.message).join(" · ")} Die übrigen Live-Daten
        bleiben sichtbar.{" "}
        <Button variant="secondary" onClick={() => retry(list)}>
          Erneut versuchen
        </Button>
      </Notice>
    );
  const forbiddenNotice = (label) => (
    <Card>
      <Badge tone="red">Kein Zugriff</Badge>
      <h2>Zugriff nicht erlaubt</h2>
      <p>
        Die Daten für {label} dürfen mit dieser Anmeldung nicht gelesen werden.
      </p>
    </Card>
  );
  return (
    <>
      <PageHeader
        title="Dashboard"
        meta={`${dashboardWeekLabel(period)} · Rolle: ${permissionLabel(user.berechtigungsstufe)}`}
      />
      <section
        className="dashboard-section"
        aria-labelledby="personal-dashboard-heading"
      >
        <h2
          id="personal-dashboard-heading"
          className="dashboard-section-heading"
        >
          Meine Ansicht
        </h2>
        {personalForbidden ? (
          forbiddenNotice("die persönliche Ansicht")
        ) : (
          <>
            {personalSources.some((state) => state.status === "loading") && (
              <Notice>Persönliche Dashboard-Daten werden geladen …</Notice>
            )}
            {failureNotice(personalFailures)}
            {staleCapacity && (
              <Notice tone="error">
                Veraltete Kapazitätsantwort: Die angeforderte Woche stimmt nicht
                mit den gespeicherten Berechnungen überein. Kapazitätswerte
                werden nicht als aktuell ausgewiesen.
              </Notice>
            )}
            <div
              className="dashboard-person-card"
              data-dashboard-component="employee-identity"
              data-testid="dashboard-person-card"
            >
              <div className="dashboard-person-avatar" aria-hidden="true">
                {employee?.kuerzel ||
                  user.name
                    ?.split(" ")
                    .map((x) => x[0])
                    .join("")}
              </div>
              <div className="dashboard-person-copy">
                <strong>{employee?.name || user.name}</strong>
                <p>
                  {personnelCategoryName} ·{" "}
                  {employee?.pensumProzent ?? "–"}% Pensum
                </p>
              </div>
            </div>
            <div className="dashboard-period-grid">
              {sections.map((section) => {
                const rows = entriesForDashboardPeriod(
                    plan.data?.items || [],
                    user.mitarbeiterId,
                    section,
                  ),
                  hours = rows.reduce((sum, item) => sum + item.stunden, 0);
                return (
                  <article
                    className="dashboard-period-card"
                    data-dashboard-component={`personal-period-${section.key}`}
                    data-period={section.key}
                    key={section.key}
                  >
                    <header className="dashboard-period-header">
                      <span className="dashboard-period-label">
                        {section.label}
                      </span>
                      <p className="dashboard-period-subtitle">
                        {section.period
                          ? `${dateCH(section.period.from)}${section.period.to !== section.period.from ? `–${dateCH(section.period.to)}` : ""}${rows.length ? ` · ${hours}h geplant` : ""}`
                          : "keine weiteren Tage diese Woche"}
                      </p>
                    </header>
                    <div className="dashboard-assignment-list">
                      {plan.status !== "success" ? (
                        <p>Daten nicht verfügbar.</p>
                      ) : rows.length ? (
                        rows.map((row) => {
                          const mandate = open.find(
                            (item) => item.id === row.mandatId,
                            ),
                            disposition = dispositionById.get(
                              row.dispositionsstatusId,
                            ),
                            dispositionLabel = disposition
                              ? dispositionPresentation(disposition.code)
                              : "Status nicht verfügbar",
                            dispositionTooltip = disposition
                              ? DISPOSITION_LABELS[disposition.code] ||
                                disposition.name ||
                                dispositionLabel
                              : "Status nicht verfügbar";
                          return (
                            <div
                              className="dashboard-assignment-row"
                              key={row.id}
                            >
                              <span
                                className={`dashboard-assignment-dot dashboard-assignment-dot-${mandateDotIndex(row.mandatId)}`}
                                aria-hidden="true"
                              />
                              {mandate ? (
                                <button
                                  type="button"
                                  className="dashboard-assignment-name"
                                  onClick={() =>
                                    navigate(
                                      `/projekte/${encodeURIComponent(mandate.id)}`,
                                    )
                                  }
                                >
                                  {mandate.name}
                                </button>
                              ) : (
                                <span className="dashboard-assignment-name dashboard-assignment-name-unresolved">
                                  Projekt nicht verfügbar
                                </span>
                              )}
                              <span
                                className="dashboard-assignment-details"
                                title={dispositionTooltip}
                              >
                                {section.period.from !== section.period.to
                                  ? `${dateCH(row.datum)} · `
                                  : ""}
                                {row.stunden}h ·{" "}
                                {dispositionLabel}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="dashboard-period-empty">
                          Keine Einsätze geplant.
                        </p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>
      <section
        className="dashboard-section dashboard-team-section"
        aria-labelledby="team-dashboard-heading"
      >
        <h2 id="team-dashboard-heading" className="dashboard-section-heading">
          Team-Ansicht
        </h2>
        {teamForbidden ? (
          forbiddenNotice("die Team-Ansicht")
        ) : (
          <>
            {loading && <Notice>Team-Dashboard-Daten werden geladen …</Notice>}
            {failureNotice(teamFailures)}
            <TeamDashboard
              navigate={navigate}
              period={period}
              staleCapacity={staleCapacity}
              cap={cap}
              plan={plan}
              mandates={mandates}
              milestones={milestones}
              confirmations={confirmations}
              confirmationStatuses={confirmationStatuses}
              offers={offers}
              offerFollowUps={offerFollowUps}
              employees={employees}
              open={open}
              unplanned={unplanned}
              deadlines={deadlines}
              externalAppointments={externalAppointments}
            />
          </>
        )}
      </section>
      <p className="data-origin">
        Wochenstunden Vollzeit: {settings?.wochenstundenVollzeit ?? "–"} h ·
        Live aus Planung und verwalteter Konfiguration
      </p>
    </>
  );
}
function TeamDashboard({
  navigate,
  period,
  staleCapacity,
  cap,
  plan,
  mandates,
  milestones,
  confirmations,
  confirmationStatuses,
  offers,
  offerFollowUps,
  employees,
  open,
  unplanned,
  deadlines,
  externalAppointments,
}) {
  const entries = plan.data?.items || [],
    attention = teamAttention({
      mandates: mandates.data?.items || [],
      entries,
      milestones: milestones.data?.items || [],
      confirmations: confirmations.data?.items || [],
    });
  const rows = filterTeamCapacity(
    cap.data || [],
    entries,
    { employeeId: "", mandatId: "", group: "" },
    open,
  );
  return (
    <>
      {staleCapacity && (
        <Notice tone="error">
          Veraltete Kapazitätsantwort: Die angeforderte Woche stimmt nicht mit
          den gespeicherten Berechnungen überein. Bitte erneut laden.
        </Notice>
      )}
      {attention.unresolvedCount > 0 && (
        <Notice tone="error">
          {attention.unresolvedCount} Datensätze haben keine auflösbare oder
          zugängliche Projektreferenz und werden weder summiert noch angezeigt.
        </Notice>
      )}
      <div className="grid grid-2 dashboard-attention" data-dashboard-row="attention">
        <section className={`card section-card dashboard-unplanned-card ${unplanned.length ? "is-alert" : "is-clear"}`} data-dashboard-component="unplanned-projects">
          <div className="dashboard-unplanned-content">
            <span className="k-label">Projekte ohne Planungseintrag</span>
            <strong className="k-value">{unplanned.length}</strong>
            {unplanned.length ? (
              <div className="dashboard-unplanned-projects" aria-label="Projekte ohne Planungseintrag">
                {unplanned.map((item) => (
                  <button
                    type="button"
                    className="dashboard-unplanned-project"
                    key={item.id}
                    onClick={() => navigate(`/projekte/${encodeURIComponent(item.id)}`)}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            ) : <span className="k-sub">alle erfasst</span>}
          </div>
        </section>
        <Card title="Kommende externe Termine" className="section-card dashboard-external-card" data-dashboard-component="external-appointments">
          <p className="period-subtitle">Heute bis Ende nächste Woche</p>
          {externalAppointments.length ? <div className="dashboard-compact-list">
            {externalAppointments.slice(0, 8).map((row, index) => <button type="button" className="dashboard-compact-row dashboard-external-row" key={`${row.mandatId}:${row.datum}`} onClick={() => navigate(`/projekte/${encodeURIComponent(row.mandate.id)}`)}>
              <span className="dashboard-avatar-group" aria-label={row.employees.map((employee) => employee.name).join(", ")}>
                {row.employees.map((employee, employeeIndex) => <span aria-hidden="true" className={`dashboard-external-avatar dashboard-avatar-${(index + employeeIndex) % 3 + 1}`} key={employee.mitarbeiterId || employee.id}>{employee.kuerzel || employee.name.split(/\s+/).map((part) => part[0]).join("")}</span>)}
              </span>
              <span className="dashboard-row-copy">{row.mandate.name}</span>
              <time className="dashboard-row-date" dateTime={row.datum}>{dateCH(row.datum)}</time>
            </button>)}
          </div> : <p className="dashboard-list-empty">Keine externen Termine in diesem Zeitraum.</p>}
        </Card>
      </div>
      <div className="grid grid-2 dashboard-attention" data-dashboard-row="operations">
        <Card
          title="Kritische Deadlines (aus Zeitplan)"
          className="section-card dashboard-deadline-card"
          data-dashboard-component="critical-deadlines"
        >
          {deadlines.length ? <div className="dashboard-compact-list">
            {deadlines.map((row) => <button type="button" className="dashboard-compact-row dashboard-deadline-row" key={row.id} onClick={() => navigate(`/projekte/${encodeURIComponent(row.mandate.id)}`)}>
              <span className="dashboard-deadline-dot" aria-label="Kritisch" />
              <span className="dashboard-row-copy">{row.mandate.name} — {row.bezeichnung}</span>
              <time className="dashboard-row-date" dateTime={row.datum}>{dateCH(row.datum)}</time>
            </button>)}
          </div> : <p className="dashboard-list-empty">Keine kritischen Deadlines im Zeitplan markiert.</p>}
        </Card>
        <DashboardTeamCapacity
          navigate={navigate}
          period={period}
          rows={rows}
        />
      </div>
      <div className="grid grid-2 dashboard-attention" data-dashboard-row="pipelines">
        <OfferPipelineCard
          navigate={navigate}
          offers={offers}
          followUps={offerFollowUps}
          employees={employees}
        />
        <ConfirmationPipelineCard
          confirmations={confirmations}
          projects={mandates}
          statusReferences={confirmationStatuses}
        />
      </div>
    </>
  );
}

const DASHBOARD_CAPACITY_EMPTY =
  "Keine aktiven Teammitglieder vorhanden. 0 h geplant.";

function DashboardTeamCapacity({ navigate, period, rows }) {
  const weekLabel = (week) =>
    week ? `${dateCH(week.von)}–${dateCH(week.bis)}` : "Woche nicht verfügbar";
  const pill = (week, index, employeeId) => {
    if (!week)
      return (
        <span
          className="dashboard-capacity-pill dashboard-capacity-pill-empty"
          aria-label={`${index ? "Nächste" : "Diese"} Woche: keine Kapazitätsdaten verfügbar`}
        >
          –
        </span>
      );
    const status = capacityStatus(week.status);
    const description = `${week.geplant} h geplant, ${week.verfuegbar} h verfügbar, ${week.auslastungProzent}% Auslastung, Status: ${status.label}, ${weekLabel(week)}`;
    return (
      <button
        type="button"
        className={`dashboard-capacity-pill ${status.className}`}
        aria-label={description}
        title={description}
        onClick={() => navigate(`/planung?mitarbeiterId=${encodeURIComponent(employeeId)}&von=${week.von}`)}
      >
        {week.geplant} h
      </button>
    );
  };
  return (
    <Card
      title="Auslastung pro Teammitglied"
      className="section-card dashboard-capacity-card"
      data-dashboard-component="team-capacity"
    >
      <p className="period-subtitle">
        Aktuelle Woche (KW{isoWeekNumber(period.from)}) und nächste Woche (KW
        {isoWeekNumber(movePlanningPeriod(period, 1))})
      </p>
      <div className="dashboard-capacity-list">
        <div className="dashboard-capacity-head" aria-hidden="true">
          <span />
          <span />
          <span>DIESE WO.</span>
          <span>NÄCHSTE WO.</span>
        </div>
        {rows.length ? (
          rows.map((row, index) => {
            const name = row.name || row.kuerzel || row.mitarbeiterId;
            const initials =
              row.kuerzel ||
              name
                .split(/\s+/)
                .map((part) => part[0])
                .join("")
                .slice(0, 2);
            return (
              <div className="dashboard-capacity-row" key={row.mitarbeiterId}>
                <span
                  className={`dashboard-capacity-avatar dashboard-capacity-avatar-${(index % 3) + 1}`}
                  aria-hidden="true"
                >
                  {initials}
                </span>
                <button
                  type="button"
                  className="dashboard-capacity-employee"
                  onClick={() =>
                    navigate(
                      `/team?mitarbeiterId=${encodeURIComponent(row.mitarbeiterId)}`,
                    )
                  }
                >
                  {name}
                </button>
                {pill(row.wochen?.[0], 0, row.mitarbeiterId)}
                {pill(row.wochen?.[1], 1, row.mitarbeiterId)}
              </div>
            );
          })
        ) : (
          <p className="empty-hint">{DASHBOARD_CAPACITY_EMPTY}</p>
        )}
      </div>
    </Card>
  );
}

function OfferPipelineCard({ navigate, offers, followUps, employees }) {
  if (offers.status === "loading")
    return (
      <Card
        title="Offerten-Pipeline"
        className="section-card dashboard-offer-pipeline"
      >
        <Notice>Offerten-Pipeline wird geladen …</Notice>
      </Card>
    );
  if (offers.status === "error")
    return (
      <Card
        title="Offerten-Pipeline"
        className="section-card dashboard-offer-pipeline"
      >
        <Notice tone="error">
          <strong>
            {offers.error?.status === 403
              ? "Kein Zugriff."
              : "Offerten konnten nicht geladen werden."}
          </strong>{" "}
          {offers.error?.status !== 403 && (
            <Button variant="secondary" onClick={offers.reload}>
              Erneut versuchen
            </Button>
          )}
        </Notice>
      </Card>
    );
  const summary = offerPipeline(
    offers.data?.items,
    followUps.status === "success" ? followUps.data?.items : [],
    employees.status === "success" ? employees.data?.items : [],
  );
  return (
    <Card
      title="Offerten-Pipeline"
      className="section-card dashboard-offer-pipeline"
      data-dashboard-component="offer-pipeline"
    >
      <p className="dashboard-pipeline-subtitle">
        {money(summary.volume)} offenes Volumen
      </p>
      {summary.total === 0 ? (
        <p className="empty-hint">Keine Offerten in der Pipeline vorhanden.</p>
      ) : (
        <>
          <div
            className="dashboard-offer-track"
            role="img"
            aria-label={`Verteilung: ${summary.open} in Bearbeitung oder offen, ${summary.sent} versendet, ${summary.won} gewonnen, ${summary.rejected} abgesagt`}
          >
            {summary.segments.map((segment) => (
              <span
                key={segment.key}
                data-segment={segment.key}
                className={`dashboard-offer-segment dashboard-pipeline-${segment.key}`}
                style={{ width: `${(segment.count / summary.total) * 100}%` }}
              />
            ))}
          </div>
          <div className="dashboard-offer-legend">
            {summary.segments.map((segment) => (
              <div key={segment.key}>
                <span>
                  <i
                    className={`dashboard-pipeline-key dashboard-pipeline-${segment.key}`}
                  />
                  {segment.label}
                </span>
                <b>{segment.count}</b>
              </div>
            ))}
          </div>
        </>
      )}
      {followUps.status === "loading" && (
        <Notice>Follow-up-Verlauf wird geladen …</Notice>
      )}
      {followUps.status === "error" && (
        <Notice tone="error">
          <strong>Offerten geladen, Follow-ups nicht verfügbar.</strong>{" "}
          Follow-up-Hinweise sind unvollständig.{" "}
          <Button variant="secondary" onClick={followUps.reload}>
            Erneut versuchen
          </Button>
        </Notice>
      )}
      {employees.status === "loading" && (
        <Notice>Verantwortliche Mitarbeitende werden geladen …</Notice>
      )}
      {employees.status === "error" && (
        <Notice tone="error">
          <strong>Mitarbeiterzuordnungen nicht verfügbar.</strong>{" "}
          <Button variant="secondary" onClick={employees.reload}>
            Erneut versuchen
          </Button>
        </Notice>
      )}
      {followUps.status === "success" && summary.attention.length > 0 && (
        <div className="dashboard-offer-followups">
          <strong>⚠ {summary.attention.length} mit fälligem Follow-up</strong>
          {summary.attention.map((item) => (
            <div key={item.offer.id}>
              <span>
                {item.offer.firma} ·{" "}
                {item.employeeName || "Verantwortliche Person nicht auflösbar"}
              </span>
              <small>
                {item.state.reason === "overdue"
                  ? `${item.state.daysOverdue} Tage über erwartetem Entscheid`
                  : "seit 14 Tagen kein Kontakt"}
              </small>
            </div>
          ))}
        </div>
      )}
      {employees.status === "success" && summary.unresolvedEmployees > 0 && (
        <Notice tone="error">
          {summary.unresolvedEmployees} verantwortliche Mitarbeiterzuordnung
          {summary.unresolvedEmployees === 1 ? " ist" : "en sind"} nicht
          auflösbar.
        </Notice>
      )}
      {summary.malformed > 0 && (
        <Notice tone="error">
          {summary.malformed} unvollständige oder unzulässige Offerte
          {summary.malformed === 1 ? " wird" : "n werden"} weder summiert noch
          beschriftet.
        </Notice>
      )}
    </Card>
  );
}

function ConfirmationPipelineCard({ confirmations, projects, statusReferences }) {
  const sources = [confirmations, projects, statusReferences];
  if (sources.some((source) => source.status === "loading"))
    return (
      <Card title="Drittbestätigungen" className="section-card">
        <Notice>Drittbestätigungen werden geladen …</Notice>
      </Card>
    );
  const failure = sources.find((source) => source.status === "error");
  if (failure)
    return (
      <Card title="Drittbestätigungen" className="section-card">
        <Notice tone="error">
          <strong>
            {failure.error?.status === 403
              ? "Kein Zugriff."
              : "Drittbestätigungen konnten nicht geladen werden."}
          </strong>{" "}
          {failure.error?.status !== 403 && (
            <Button variant="secondary" onClick={failure.reload}>
              Erneut versuchen
            </Button>
          )}
        </Notice>
      </Card>
    );
  const summary = groupConfirmations(
    confirmations.data?.items,
    projects.data?.items,
    statusReferences.data?.items,
  );
  return (
    <Card
      title="Drittbestätigungen"
      className="section-card dashboard-confirmation-pipeline"
      data-dashboard-component="third-party-confirmations"
    >
      <p className="dashboard-pipeline-subtitle">
        {summary.total} insgesamt über alle Projekte
      </p>
      <div className="dashboard-confirmation-statuses">
        {CONFIRMATION_STATUSES.map((status) => {
          const count = summary.groups[status].length;
          return (
            <div className="dashboard-confirmation-status" key={status}>
              <div className="dashboard-confirmation-status-head">
                <span>
                  <i
                    className={`dashboard-confirmation-key dashboard-confirmation-${status.split(" ")[0]}`}
                  />
                  {status}
                </span>
                <b>{count}</b>
              </div>
              <div
                className="dashboard-confirmation-track"
                role="img"
                aria-label={`${status}: ${count} von ${summary.total}`}
              >
                <span
                  className={`dashboard-confirmation-fill dashboard-confirmation-${status.split(" ")[0]}`}
                  style={{
                    width: `${summary.total ? (count / summary.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      {summary.total === 0 && (
        <p className="empty-hint">Keine Drittbestätigungen vorhanden.</p>
      )}
      {summary.rejectedCount > 0 && (
        <Notice tone="error">
          Datenqualitätswarnung: {summary.rejectedCount} Drittbestätigung{summary.rejectedCount === 1 ? "" : "en"} wird{summary.rejectedCount === 1 ? "" : " werden"} nicht gezählt ({summary.orphaned.length} ohne auflösbares Projekt, {summary.invalid.length} mit ungültigem oder inaktivem Status).
        </Notice>
      )}
    </Card>
  );
}

const specs = {
  projekte: {
    path: "/mandates",
    title: "Projekte",
    create: "Neues Projekt",
    fields: [
      ["name", "Name", "text", 1],
      ["uid", "UID-Nr."],
      ["gruppe", "Gruppe"],
      ["mandatsleitungId", "Mandatsleitung", "employee", 1],
      ["budget", "Budget (h)", "number"],
    ],
    columns: [
      { key: "abacusNr", label: "Projekt-ID" },
      { key: "name", label: "Name / Firma" },
      { key: "uid", label: "UID-Nr.", render: (r) => r.uid || "–" },
      { key: "gruppe", label: "Gruppe", render: (r) => r.gruppe || "–" },
      { key: "budget", label: "Budget", render: (r) => projectBudget(r.budget) === null ? "–" : money(r.budget) },
    ],
  },
  bestaetigungen: {
    path: "/confirmations",
    title: "Bestätigungen",
    create: "Neue Bestätigung",
    fields: [
      ["mandatId", "Mandat", "mandate", 1],
      [
        "bestaetigungsartId",
        "Bestätigungsart",
        "reference",
        1,
        "bestaetigungsarten",
      ],
      [
        "bestaetigungsstatusId",
        "Bestätigungsstatus",
        "reference",
        1,
        "bestaetigungsstatus",
      ],
      ["faelligAm", "Fällig am", "date"],
    ],
    columns: [
      { key: "mandatId", label: "Mandat" },
      { key: "bestaetigungsartId", label: "Bestätigungsart" },
      { key: "bestaetigungsstatusId", label: "Status" },
      {
        key: "faelligAm",
        label: "Fällig am",
        render: (r) => dateCH(r.faelligAm),
      },
    ],
  },
  offerten: {
    path: "/offers",
    title: "Offerten",
    create: "Neue Offerte",
    fields: [
      ["firma", "Firma", "text", 1],
      ["uid", "UID"],
      ["verantwortlichId", "Verantwortlich", "employee", 1],
      ["empfohlenDurchKanal", "Empfohlen durch / Kanal"],
      ["kontaktperson", "Kontaktperson"],
      ["kontaktEmail", "E-Mail", "email"],
      ["kontaktTelefon", "Telefon", "tel"],
      ["dienstleistung", "Offerierte Dienstleistung", "text", 1],
      ["betragVon", "Betrag von (CHF)", "number", 1],
      ["betragBis", "Betrag bis (CHF)", "number", 1],
      ["datumVersand", "Versanddatum", "date"],
      ["datumErwarteterEntscheid", "erwarteter Entscheid", "date", 1],
      [
        "entscheid",
        "Entscheid",
        "select",
        1,
        ["offen", "versendet", "gewonnen", "abgesagt"],
      ],
      ["entscheidDatum", "Entscheiddatum", "date"],
      ["entscheidKommentar", "Entscheidkommentar"],
    ],
    columns: [
      { key: "firma", label: "Firma" },
      { key: "dienstleistung", label: "Dienstleistung" },
      {
        key: "betragBis",
        label: "Betrag bis",
        render: (r) => money(r.betragBis),
      },
      {
        key: "datumErwarteterEntscheid",
        label: "Erwarteter Entscheid",
        render: (r) => dateCH(r.datumErwarteterEntscheid),
      },
      {
        key: "entscheid",
        label: "Entscheid",
        render: (r) => (
          <Badge tone={r.entscheid === "abgesagt" ? "red" : "navy"}>
            {r.entscheid}
          </Badge>
        ),
      },
    ],
  },
  team: {
    path: "/employees",
    title: "Team",
    create: "Neues Teammitglied",
    fields: [
      ["kuerzel", "Kürzel", "text", 1],
      ["name", "Name", "text", 1],
      [
        "personalkategorieId",
        "Personalkategorie",
        "reference",
        1,
        "personalkategorien",
      ],
      ["pensumProzent", "Pensum (%)", "number", 1],
      ["freieWochentage", "Freie Wochentage"],
    ],
    columns: [
      { key: "kuerzel", label: "Kürzel" },
      { key: "name", label: "Name" },
      { key: "personalkategorieId", label: "Personalkategorie" },
      {
        key: "pensumProzent",
        label: "Pensum",
        render: (r) => `${r.pensumProzent}%`,
      },
    ],
  },
};
function ProjectPortfolio({ route, navigate, user }) {
  const projects = useData("/mandates?limit=1000"),
    planning = useData("/planning-entries?limit=1000"),
    employees = useData("/employees?limit=1000"),
    phases = useData("/reference-data/phasen?limit=100"),
    dispositions = useData("/reference-data/dispositionsstatus?limit=100"),
    confirmations = useData("/confirmations?limit=1000"),
    [record, setRecord] = useState(null),
    [creating, setCreating] = useState(false),
    [showImport, setShowImport] = useState(false),
    [actionError, setActionError] = useState(""),
    [portfolioFilters, setPortfolioFilters] = useState(() =>
      projectFilterFromSearch(route.search),
    );
  const references = useMemo(
    () => ({
      phases: phases.data?.items || [],
      dispositions: dispositions.data?.items || [],
    }),
    [phases.data, dispositions.data],
  );
  const filters = portfolioFilters,
    planningYear = filters.planningYear ?? planningYearFor(new Date().toISOString().slice(0, 10)),
    all = useMemo(
      () =>
        decorateProjects(
          projects.data?.items,
          planning.data?.items,
          employees.data?.items,
          new Date(),
          references,
          planningYear,
        ),
      [projects.data, planning.data, employees.data, references, planningYear],
    ),
    rows = sortProjects(
      filterProjects(all, filters),
      filters.sort,
      filters.direction,
    ),
    activeFilters = Boolean(
      filters.q ||
      filters.phaseId ||
      filters.status ||
      filters.disposition ||
      filters.mandatsleitungId ||
      filters.gruppe ||
      filters.ohnePlanung,
    ),
    detailId = route.pathname?.split("/")[2],
    states = [projects, planning, employees, phases, dispositions, confirmations],
    failures = states.filter((x) => x.status === "error"),
    loading = states.some((x) => x.status === "loading");
  const update = (changes) => {
    const next = { ...filters, ...changes };
    setPortfolioFilters(next);
    const search = projectFilterSearch(next);
    navigate(`/projekte${search ? `?${search}` : ""}`);
  };
  const resetFilters = () => {
    const next = { ...filters, q: "", phaseId: "", status: "", disposition: "", mandatsleitungId: "", gruppe: "", ohnePlanung: false };
    setPortfolioFilters(next);
    const search = projectFilterSearch(next);
    navigate(`/projekte${search ? `?${search}` : ""}`);
  };
  const open = (row) => navigate(`/projekte/${row.id}${route.search || ""}`),
    close = () => {
      setCreating(false);
      navigate(`/projekte${route.search || ""}`);
    },
    retry = () => failures.forEach((x) => x.reload());
  useEffect(() => {
    if (!detailId) {
      setRecord(null);
      return;
    }
    if (projects.status === "success")
      setRecord((current) =>
        all.find((x) => x.id === detailId) ||
        (current?.id === detailId
          ? current
          : { forbidden: true, id: detailId }),
      );
  }, [detailId, projects.status, projects.data, all]);
  const groups = [...new Set(all.map((x) => x.gruppe).filter(Boolean))].sort(),
    planningYears = [...new Set([
      planningYear - 2, planningYear - 1, planningYear, planningYear + 1, planningYear + 2,
      ...(planning.data?.items || []).map((entry) => planningYearFor(entry.datum)),
    ])].sort((a, b) => a - b),
    leaders = [
      ...new Map(
        all
          .filter((x) => x.mandatsleitungId)
          .map((x) => [x.mandatsleitungId, [x.leaderName, x.leaderAbbreviation && `(${x.leaderAbbreviation})`].filter(Boolean).join(" ")]),
      ).entries(),
    ];
  const phaseNames = Object.fromEntries(
    references.phases.map((phase) => [phase.code || phase.name, phase.name]),
  ),
    availableProjectPhases = [
      ...new Set(all.flatMap((project) => project.snapshot.phases)),
    ].sort((a, b) => a.localeCompare(b, "de-CH", { numeric: true }));
  const columns = [
    { key: "name", label: "Projekt", value: (r) => r.name },
    { key: "abacusNr", label: "Projekt-ID", value: (r) => r.abacusNr },
    { key: "typ", label: "Typ", value: (r) => r.typ },
    { key: "gruppe", label: "Gruppe", value: (r) => r.gruppe || "–" },
    { key: "uid", label: "UID-Nr", value: (r) => r.uid || "–" },
    {
      key: "mandatsleitung",
      label: "Mandatsleitung",
      value: (r) => r.leaderName,
      render: (r) => (
        <span className="project-leader">
          {r.leaderName}
          <small>{r.mandatsleitungId && ` (${r.mandatsleitungId})`}</small>
        </span>
      ),
    },
    {
      key: "phasen",
      label: "Phase(n)",
      value: (r) => r.snapshot.phases.join(", ") || "–",
    },
    {
      key: "disposition",
      label: "Disposition",
      value: (r) => r.snapshot.disposition,
      render: (r) => (
        <span title={DISPOSITION_LABELS[r.snapshot.disposition]}>
          {r.snapshot.disposition}
        </span>
      ),
    },
    {
      key: "budget",
      label: "Budget (h)",
      value: (r) => String(r.budget ?? ""),
    },
    {
      key: "geplant",
      label: "Geplant (h)",
      value: (r) => String(r.snapshot.plannedHours),
    },
    { key: "kommentar", label: "Kommentar", value: (r) => r.kommentar || "" },
    { key: "intern", label: "Intern", value: (r) => r.intern ? "Ja" : "Nein" },
  ];
  const exportExcel = () => {
    const headings = [
        "Projekt-ID (Abacus)",
        "Projektname",
        "Gruppe",
        "UID-Nr",
        "Mandatsleitung (Kürzel)",
        "Phase(n)",
        "Disposition",
        "Budget (h)",
        "Geplant (h)",
        "Kommentar",
        "Internes Projekt",
      ],
      values = rows.map((row) => [
        row.abacusNr,
        row.name,
        row.gruppe || "",
        row.uid || "",
        row.mandatsleitungId,
        row.snapshot.phases.join(", "),
        row.snapshot.disposition,
        projectBudget(row.budget) ?? "",
        row.snapshot.plannedHours,
        row.kommentar,
        row.intern ? "Ja" : "Nein",
      ]);
    import("xlsx")
      .then((XLSX) => {
        const book = XLSX.utils.book_new(),
          sheet = XLSX.utils.aoa_to_sheet([headings, ...values]);
        XLSX.utils.book_append_sheet(book, sheet, "Projekte");
        XLSX.writeFile(
          book,
          `Abelia_Projekte_Export_${new Date().toISOString().slice(0, 10)}.xlsx`,
        );
      })
      .catch((error) =>
        setActionError(`Export fehlgeschlagen: ${error.message}`),
      );
  };
  const downloadTemplate = () =>
    api("/imports/mandates/template")
      .then(downloadMandateTemplate)
      .catch((error) =>
        setActionError(`Vorlage fehlgeschlagen: ${error.message}`),
      );
  if (failures.some((x) => x.error?.status === 403) && !all.length)
    return (
      <>
        <PageHeader title="Projekte" meta="Authentifizierte Portfolio-API" />
        <Card>
          <Badge tone="red">Kein Zugriff</Badge>
          <h2>Zugriff nicht erlaubt</h2>
          <p>
            Projektkennungen und Portfolio-Daten dürfen mit dieser Anmeldung
            nicht gelesen werden.
          </p>
        </Card>
      </>
    );
  return (
    <>
      <PageHeader
        title="Projekte"
        meta={`${rows.length} von ${all.length} Projekten`}
      />
      {loading && <Notice>Daten werden geladen …</Notice>}
      {failures.length > 0 && (
        <Notice tone="error">
          <strong>
            {all.length
              ? "Teilweise/veraltete Daten"
              : "Portfolio konnte nicht geladen werden"}
            .
          </strong>{" "}
          {failures.map((x) => x.error?.message).join(" · ")}{" "}
          {all.length && (
            <>
              Stand: {projects.receivedAt?.toLocaleString("de-CH")}. Die letzte
              erfolgreiche Antwort bleibt sichtbar.
            </>
          )}{" "}
          <Button variant="secondary" onClick={retry}>
            Erneut versuchen
          </Button>
        </Notice>
      )}
      {actionError && (
        <Notice tone="error" onDismiss={() => setActionError("")}>
          {actionError}
        </Notice>
      )}
      <div className="project-filters">
        <input
          className="project-filter-search"
          aria-label="Projekte durchsuchen"
          placeholder="Suche Projekt, Kunde, Abacus-Nr., UID …"
          value={filters.q}
          onChange={(e) => update({ q: e.target.value })}
        />
        <select
          aria-label="Phase"
          value={filters.phaseId}
          onChange={(e) => update({ phaseId: e.target.value })}
        >
          <option value="">Alle Phasen</option>
          {availableProjectPhases.map((x) => (
            <option key={x}>
              {x}
              {phaseNames[x] && phaseNames[x] !== x
                ? ` — ${phaseNames[x]}`
                : ""}
            </option>
          ))}
        </select>
        <select
          aria-label="Mandatsleitung"
          value={filters.mandatsleitungId}
          onChange={(e) => update({ mandatsleitungId: e.target.value })}
        >
          <option value="">Alle Mandatsleitungen</option>
          {leaders.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <select
          aria-label="Gruppe"
          value={filters.gruppe}
          onChange={(e) => update({ gruppe: e.target.value })}
        >
          <option value="">Alle Gruppen</option>
          {groups.map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>
        <label className="project-filter-checkbox">
          <input
            type="checkbox"
            checked={filters.ohnePlanung}
            onChange={(e) => update({ ohnePlanung: e.target.checked })}
          />{" "}
          Nur ohne Planungseintrag
        </label>
        <select
          aria-label="Planjahr"
          value={planningYear}
          onChange={(e) => update({
            planningYear: Number(e.target.value),
            phaseId: "",
          })}
        >
          {planningYears.map((year) => <option key={year} value={year}>{planningYearLabel(year)}</option>)}
        </select>
        {activeFilters && (
          <Button
            variant="secondary"
            onClick={resetFilters}
          >
            Filter zurücksetzen
          </Button>
        )}
        <details className="project-more-filters">
          <summary>Weitere Filter</summary>
          <div>
            <span>Produkterweiterungen</span>
            <select aria-label="Status" value={filters.status} onChange={(e) => update({ status: e.target.value })}>
              <option value="">Alle Status</option><option value="aktiv">aktiv</option><option value="archiviert">archiviert</option>
            </select>
            <select aria-label="Disposition" value={filters.disposition} onChange={(e) => update({ disposition: e.target.value })}>
              <option value="">Alle Dispositionen</option>
              {Object.keys(DISPOSITION_LABELS).map((value) => <option key={value}>{value}</option>)}
            </select>
          </div>
        </details>
      </div>
      <div className="toolbar project-actions">
        <Tabs
          label="Projektansicht"
          value={filters.view}
          onChange={(view) => update({ view }, true)}
          items={[
            { value: "cards", label: "Karten" },
            { value: "table", label: "Tabelle" },
          ]}
        />
        <Button variant="secondary" onClick={exportExcel}>
          Als Excel exportieren
        </Button>
        {["administrator", "manager", "consultant"].includes(user?.berechtigungsstufe) && (
          <Button onClick={() => setCreating(true)}>+ Neues Projekt</Button>
        )}
        {["administrator", "manager"].includes(user?.berechtigungsstufe) && (
          <>
            <Button variant="secondary" onClick={downloadTemplate}>
              Import-Vorlage herunterladen
            </Button>
            <Button onClick={() => setShowImport(true)}>
              Datei importieren
            </Button>
          </>
        )}
      </div>
      {!loading && !all.length && !failures.length ? (
        <Card>
          <p className="empty-state">Noch keine Projekte vorhanden.</p>
        </Card>
      ) : !loading && !rows.length ? (
        <Card>
          <p className="empty-state">
            Keine Projekte für diese Filterkombination.
          </p>
        </Card>
      ) : filters.view === "table" ? (
        <ProjectPortfolioTable
          columns={columns}
          rows={rows}
          onOpen={open}
          sort={filters.sort}
          direction={filters.direction}
          onSort={(key) =>
            update({
              sort: key,
              direction:
                filters.sort === key && filters.direction === "asc"
                  ? "desc"
                  : "asc",
            })
          }
        />
      ) : (
        <div className="project-card-grid">
          {rows.map((row) => (
            <article
              key={row.id}
              className="project-card"
              role="link"
              tabIndex="0"
              aria-label={`${row.name} öffnen`}
              onClick={() => open(row)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  open(row);
                }
              }}
            >
              <header>
                <strong className="project-card-name">{row.name}</strong>
                <span className="project-phase-badges">
                  {row.snapshot.phases.map((phase) => (
                    <span
                      key={phase}
                      className={`project-phase project-phase-${phase.toLowerCase()}`}
                      title={phaseNames[phase] || phase}
                    >
                      {phase}
                    </span>
                  ))}
                </span>
              </header>
              <p>
                Projekt-ID {row.abacusNr} · Mandatsleitung: {row.leaderName}
                {row.mandatsleitungId && (
                  <small> ({row.mandatsleitungId})</small>
                )}
              </p>
              <p>
                Disposition:{" "}
                <b title={DISPOSITION_LABELS[row.snapshot.disposition]}>
                  {row.snapshot.disposition}
                </b>
              </p>
              <p>{[row.gruppe, row.uid].filter(Boolean).join(" · ") || "Keine Gruppe oder UID hinterlegt"}</p>
              {projectBudget(row.budget) === null ? (
                <p>{row.snapshot.plannedHours}h geplant · Kein Budget hinterlegt</p>
              ) : (
                <>
                  <div
                    className="project-budget-track"
                    role="img"
                    aria-label={`${row.snapshot.plannedHours} von ${row.budget} Stunden geplant`}
                  >
                    <span
                      className={row.snapshot.plannedHours > row.budget ? "over" : ""}
                      style={{ width: `${Math.min(100, (row.snapshot.plannedHours / (row.budget || 1)) * 100)}%` }}
                    />
                  </div>
                  <p>
                    {row.snapshot.plannedHours}h geplant / {row.budget}h Budget{" "}
                    {row.snapshot.plannedHours > row.budget && " · Budget überschritten"}
                  </p>
                </>
              )}
              <small>
                {row.snapshot.plannedHours === 0
                  ? "⚠ Noch kein Planungseintrag"
                  : `▣ ${row.snapshot.plannedHours}h in der Planung eingeplant (${planningYearLabel(planningYear)})`}
              </small>
              {row.kommentar && (
                <small>
                  ●{" "}
                  {row.kommentar.length > 70
                    ? `${row.kommentar.slice(0, 70)}…`
                    : row.kommentar}
                </small>
              )}
            </article>
          ))}
        </div>
      )}
      {showImport && (
        <MandateImportDialog
          onClose={() => setShowImport(false)}
          onImported={() => projects.reload()}
        />
      )}{" "}
      {record?.forbidden && (
        <Dialog title="Projekt nicht verfügbar" onClose={close}>
          <Notice tone="error">
            Die Projektkennung ist ungültig, nicht vorhanden oder für diese
            Anmeldung nicht zugänglich.
          </Notice>
          <Button variant="secondary" onClick={close}>
            Zurück zum Portfolio
          </Button>
        </Dialog>
      )}
      {(creating || (record && !record.forbidden)) && (
        <ProjectDetailDialog
          project={creating ? {} : record}
          employees={employees.data?.items || []}
          user={user}
          onClose={close}
          onSaved={(saved) => {
            const next = {
              ...(creating ? {} : record),
              ...saved,
              snapshot: creating ? undefined : record?.snapshot,
            };
            setCreating(false);
            setRecord(next);
            projects.reload();
            planning.reload();
            if (saved.id && saved.id !== detailId)
              navigate(`/projekte/${saved.id}${route.search || ""}`);
          }}
          planningYear={planningYear}
        >
          {(displayedProject) => displayedProject?.id && (
            <ProjectDetails project={displayedProject} navigate={navigate} planningYear={planningYear} showOverviewHeading={false} />
          )}
        </ProjectDetailDialog>
      )}
    </>
  );
}
function ResourceView({ route, navigate, user }) {
  const spec = specs[route.id],
    isOffers = route.id === "offerten",
    isConfirmations = route.id === "bestaetigungen",
    records = useData(`${spec.path}?limit=100`),
    followUps = useData(isOffers ? "/offer-follow-ups?limit=100" : null),
    employees = useData(isOffers ? "/employees?limit=100" : null),
    mandates = useData(isConfirmations ? "/mandates?limit=100" : null),
    [query, setQuery] = useState(""),
    [record, setRecord] = useState(null),
    [offerMoves, setOfferMoves] = useState({}),
    [offerMoveError, setOfferMoveError] = useState(""),
    [offerMovePending, setOfferMovePending] = useState(new Set()),
    [dragTargetStatus, setDragTargetStatus] = useState(null),
    draggedOfferRef = useRef(null),
    params = new URLSearchParams(route.search),
    mandate = params.get("mandatId"),
    employeeFilter = params.get("mitarbeiterId"),
    detailId = route.pathname?.split("/")[2];
  const offerFollowUps = followUps.data?.items || [],
    persisted = records.data?.items || [],
    all = persisted.map((item) => offerMoves[item.id] ? { ...item, ...offerMoves[item.id] } : item),
    employeeNames = Object.fromEntries(
      (employees.data?.items || []).map((item) => [item.id, item.name]),
    ),
    withState = all.map((offer) => ({
      offer,
      state: offerFollowUpState(offer, offerFollowUps),
    }));
  useEffect(() => {
    const targetId = detailId || (route.id === "team" ? employeeFilter : null);
    if (!targetId) {
      setRecord(null);
      return;
    }
    if (records.status === "success")
      setRecord(
        (() => {
          const item = (records.data?.items || []).find(
            (candidate) => candidate.id === targetId,
          );
          return item
            ? { ...item, ...offerMoves[targetId] }
            : { forbidden: true, id: targetId };
        })(),
      );
  }, [
    detailId,
    employeeFilter,
    route.id,
    records.status,
    records.data,
    offerMoves,
  ]);
  const rows = all.filter(
      (row) =>
        (!mandate || row.mandatId === mandate) &&
        (!employeeFilter || row.id === employeeFilter) &&
        Object.values(row).some((value) =>
            String(value ?? "")
              .toLowerCase()
              .includes(query.toLowerCase()),
          ),
    ),
    mandateNames = Object.fromEntries(
      (mandates.data?.items || []).map((item) => [item.id, item.name]),
    ),
    columns = isConfirmations
      ? spec.columns.map((column) =>
          column.key === "mandatId"
            ? {
                ...column,
                render: (row) => (
                  <button
                    className="table-link"
                    onClick={() => navigate(`/projekte/${row.mandatId}`)}
                  >
                    {mandateNames[row.mandatId] || row.mandatId}
                  </button>
                ),
              }
            : column,
        )
      : spec.columns,
    dueOffers = withState.filter(
      (item) => item.state.due && rows.some((row) => row.id === item.offer.id),
    );
  const open = (row) => {
      if (isOffers) {
        setRecord(row);
        navigate(`/offerten/${row.id}${route.search || ""}`);
      } else setRecord(row);
    },
    close = () =>
      isOffers && detailId
        ? navigate(`/offerten${route.search || ""}`)
        : setRecord(null),
    offerColumns = OFFER_STATUSES.map(({ value, label }) => [value, label]);
  const moveOffer = async (offer, nextStatus) => {
    if (
      offer.mandatId ||
      offerMovePending.has(offer.id) ||
      !canMoveOffer(offerStatus(offer), nextStatus)
    )
      return;
    const patch = offerStatusPatch(offer, nextStatus);
    setOfferMoveError("");
    setOfferMovePending((pending) => new Set(pending).add(offer.id));
    setOfferMoves((moves) => ({ ...moves, [offer.id]: patch }));
    try {
      const saved = await api(`/offers/${offer.id}`, {
        method: "PATCH",
        body: JSON.stringify({ ...patch, version: offer.version }),
        successMessage: "Offertenstatus gespeichert.",
      });
      setOfferMoves((moves) => ({
        ...moves,
        [offer.id]: { ...offer, ...patch, ...saved, id: offer.id },
      }));
    } catch (error) {
      setOfferMoves((moves) => {
        const next = { ...moves };
        delete next[offer.id];
        return next;
      });
      setOfferMoveError(
        error.status === 409
          ? "Versionskonflikt: Die Offerte wurde neu geladen."
          : `Statusänderung fehlgeschlagen: ${error.message}`,
      );
      if (error.status === 409) records.reload();
    } finally {
      setOfferMovePending((pending) => {
        const next = new Set(pending);
        next.delete(offer.id);
        return next;
      });
    }
  };
  const startOfferDrag = (event, offer, status) => {
    if (offer.mandatId || offerMovePending.has(offer.id)) {
      event.preventDefault();
      return;
    }
    draggedOfferRef.current = { id: offer.id, status };
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", offer.id);
    event.dataTransfer.setData("text/offer-id", offer.id);
    event.dataTransfer.setData("text/offer-status", status);
  };
  const allowOfferDrop = (event, status) => {
    const dragged = draggedOfferRef.current;
    if (!dragged || !canMoveOffer(dragged.status, status)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (dragTargetStatus !== status) setDragTargetStatus(status);
  };
  const dropOffer = (event, status) => {
    event.preventDefault();
    const dragged = draggedOfferRef.current;
    draggedOfferRef.current = null;
    setDragTargetStatus(null);
    if (!dragged || !canMoveOffer(dragged.status, status)) return;
    const offer = rows.find((row) => row.id === dragged.id);
    if (offer) moveOffer(offer, status);
  };
  const finishOfferDrag = () => {
    draggedOfferRef.current = null;
    setDragTargetStatus(null);
  };
  return (
    <>
      <PageHeader
        title={spec.title}
        meta={
          isOffers
            ? `${rows.length} Offerten · ${all.filter((item) => offerStatus(item) === "offen").length} in Bearbeitung / offen`
            : `${rows.length} Einträge aus sicherer Persistenz`
        }
      />
      <div className={`toolbar${isOffers ? " offer-toolbar" : ""}`}>
        <input
            className="filter-input"
            aria-label={`${spec.title} durchsuchen`}
            placeholder={`${spec.title} durchsuchen …`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        {(route.id !== "team" ||
          user?.berechtigungsstufe === "administrator") && (
          <Button onClick={() => setRecord({})}>+ {spec.create}</Button>
        )}
      </div>
      {dueOffers.length > 0 && (
        <section className="followup-banner" aria-label="Fällige Follow-ups">
          <strong>
            ⚠ {dueOffers.length} Offerte{dueOffers.length > 1 ? "n" : ""} mit
            fälligem Follow-up
          </strong>
          {dueOffers.map(({ offer, state }) => (
            <button key={offer.id} onClick={() => open(offer)}>
              <span>
                <b>{offer.firma}</b> ·{" "}
                {employeeNames[offer.verantwortlichId] ||
                  offer.verantwortlichId}
              </span>
              <span>
                {state.reason === "overdue"
                  ? `${state.daysOverdue} Tage über erwartetem Entscheid`
                  : "seit über 2 Wochen kein Follow-up"}
              </span>
            </button>
          ))}
        </section>
      )}
      {offerMoveError && (
        <Notice tone="error">
          <strong>
            Änderung zurückgerollt.
          </strong>{" "}
          {offerMoveError}
        </Notice>
      )}
      <LoadState state={records}>
        {isOffers ? (
          <>
            <p className="offer-board-help" id="offer-board-help">
              Mit Maus Karte ziehen; auf Touch- und Tastaturgeräten den Status direkt über „Status ändern“ wählen. Der Status wird direkt gespeichert.
            </p>
            <div className="offer-board" aria-describedby="offer-board-help">
            {offerColumns.map(([status, label]) => {
              const offers = rows
                .filter((row) => offerStatus(row) === status)
                .sort((a, b) => String(b.datumVersand || "").localeCompare(String(a.datumVersand || "")));
              return (
                <section
                  className={`offer-column offer-column-${status}${dragTargetStatus === status ? " is-drop-target" : ""}`}
                  key={status}
                  aria-label={label}
                  onDragOver={(event) => allowOfferDrop(event, status)}
                  onDrop={(event) => dropOffer(event, status)}
                >
                  <header>
                    <strong>{label.toUpperCase()}</strong>
                    <span>{offers.length}</span>
                  </header>
                  {!offers.length && (
                    <p className="empty-hint">Keine Einträge</p>
                  )}
                  {offers.map((offer) => {
                    const due = withState.find(
                      (item) => item.offer.id === offer.id,
                    )?.state.due;
                    return (
                      <div
                        className="offer-card"
                        key={offer.id}
                        role="button"
                        tabIndex={0}
                        draggable={!offer.mandatId && !offerMovePending.has(offer.id)}
                        aria-busy={offerMovePending.has(offer.id)}
                        title={
                          offer.mandatId
                            ? "Diese Offerte ist bereits in ein Projekt umgewandelt."
                            : "Ziehen, um den Offertenstatus zu ändern."
                        }
                        onDragStart={(event) =>
                          startOfferDrag(event, offer, status)
                        }
                        onDragEnd={finishOfferDrag}
                        onClick={() => open(offer)}
                        onKeyDown={(event) => { if (["Enter", " "].includes(event.key) && event.target === event.currentTarget) { event.preventDefault(); open(offer); } }}
                      >
                        <strong>{offer.firma}</strong>
                        <span>
                          {employeeNames[offer.verantwortlichId] ||
                            offer.verantwortlichId}{" "}
                          · {offer.dienstleistung}
                        </span>
                        {offer.empfohlenDurchKanal && (
                          <span>Empfohlen durch / Kanal: {offer.empfohlenDurchKanal}</span>
                        )}
                        <span>
                          Versand: {dateCH(offer.datumVersand)} · Entscheid
                          erw.: {dateCH(offer.datumErwarteterEntscheid)}
                        </span>
                        <b>
                          {money(offer.betragVon)}–
                          {money(offer.betragBis).replace("CHF ", "")}
                        </b>
                        {due && <em>⚠ Follow-up fällig</em>}
                        {status === "abgesagt" && offer.entscheidKommentar && (
                          <em>{offer.entscheidKommentar}</em>
                        )}
                        {status === "gewonnen" && offer.mandatId && (
                          <em>✓ Projekt angelegt</em>
                        )}
                        <label className="offer-status-select" onClick={(event) => event.stopPropagation()}>
                          <span>Status ändern</span>
                          <select aria-label={`Status von ${offer.firma} ändern`} value={status} disabled={Boolean(offer.mandatId) || offerMovePending.has(offer.id)} onChange={(event) => moveOffer(offer, event.target.value)}>
                            <option value={status}>{label}</option>
                            {OFFER_STATUSES.filter((item) => canMoveOffer(status, item.value)).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                          </select>
                        </label>
                      </div>
                    );
                  })}
                </section>
              );
            })}
            </div>
          </>
        ) : (
          <Card>
            <DataTable
              columns={[
                ...columns,
                {
                  key: "action",
                  label: "",
                  render: (r) => (
                    <Button variant="secondary" onClick={() => open(r)}>
                      Details / Bearbeiten
                    </Button>
                  ),
                },
              ]}
              rows={rows}
              empty={`Noch keine ${spec.title} vorhanden.`}
            />
          </Card>
        )}
      </LoadState>
      {record?.forbidden && (
        <Dialog title="Offerte nicht verfügbar" onClose={close}>
          <Notice tone="error">
            Die Offertenkennung ist ungültig, nicht vorhanden oder für diese
            Anmeldung nicht zugänglich.
          </Notice>
          <Button variant="secondary" onClick={close}>
            Zurück zu Offerten
          </Button>
        </Dialog>
      )}
      {record && !record.forbidden && (
        <ResourceDialog
          spec={spec}
          record={record}
          user={user}
          navigate={navigate}
          onClose={close}
          onSaved={() => {
            setRecord(null);
            records.reload();
            followUps.reload();
            if (detailId) navigate(`/offerten${route.search || ""}`);
          }}
        />
      )}
    </>
  );
}

const TEAM_DAYS = ["Mo", "Di", "Mi", "Do", "Fr"];

export function TeamView({ route, user }) {
  const isAdministrator = user?.berechtigungsstufe === "administrator";
  const records = useData("/employees?limit=100"),
    accounts = useData(isAdministrator ? "/users?limit=100" : null),
    personnel = useData("/reference-data/personalkategorien?limit=100"),
    [record, setRecord] = useState(null),
    employeeFilter = new URLSearchParams(route.search).get("mitarbeiterId");
  const employees = (records.data?.items || []).filter(
    (employee) => employee.aktiv !== false,
  );
  const categories = personnel.data?.items || [];
  const categoryNames = Object.fromEntries(
    categories.map((item) => [item.id, item.name]),
  );
  useEffect(() => {
    if (!employeeFilter || records.status !== "success") return;
    setRecord(
      employees.find((item) => item.id === employeeFilter) || {
        forbidden: true,
      },
    );
  }, [employeeFilter, records.status, records.data]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <PageHeader
        title="Teammitglieder"
        meta={`${employees.length} Teammitglieder`}
      />
      <div className="team-toolbar">
        <Button onClick={() => setRecord({})}>+ Neues Teammitglied</Button>
      </div>
      <LoadState state={records}>
        <div className="team-grid">
          {employees.map((employee, index) => (
            <button
              className={`team-member-card team-color-${index % 10}`}
              key={employee.id}
              onClick={() => setRecord(employee)}
            >
              <span className="team-avatar">{employee.kuerzel}</span>
              <strong>{employee.name}</strong>
              <span>
                {categoryNames[employee.personalkategorieId] ||
                  employee.personalkategorieId}{" "}
                · {employee.pensumProzent}%
              </span>
              {isAdministrator && <b>✎ Bearbeiten</b>}
            </button>
          ))}
        </div>
        {!employees.length && (
          <Card>
            <p className="empty-state">Noch keine Teammitglieder vorhanden.</p>
          </Card>
        )}
      </LoadState>
      {record?.forbidden && (
        <Dialog
          title="Teammitglied nicht verfügbar"
          onClose={() => setRecord(null)}
        >
          <Notice tone="error">Das Teammitglied wurde nicht gefunden.</Notice>
        </Dialog>
      )}
      {record && !record.forbidden && (
        <TeamDialog
          record={record}
          account={(accounts.data?.items || []).find((item) => item.mitarbeiterId === record.id)}
          categories={categories}
          user={user}
          onClose={() => setRecord(null)}
          onSaved={() => {
            setRecord(null);
            records.reload();
            accounts.reload();
          }}
        />
      )}
    </>
  );
}

export function TeamDialog({ record, account, categories, user, onClose, onSaved }) {
  const isAdministrator = user?.berechtigungsstufe === "administrator";
  const readOnly = Boolean(record.id) && !isAdministrator;
  const allowedCategories = categories.filter((item) => item.aktiv !== false && (record.id && isAdministrator ? true : item.mitarbeitertyp === "extern"));
  const externalCategory = allowedCategories.find((item) => item.mitarbeitertyp === "extern");
  const initial = () => ({
    kuerzel: record.kuerzel || "",
    name: record.name || "",
    personalkategorieId: record.personalkategorieId || (!record.id ? externalCategory?.id || "" : ""),
    pensumProzent: record.pensumProzent ?? 100,
    freieWochentage: record.freieWochentage || [],
  });
  const [form, setForm] = useState(initial),
    [error, setError] = useState(""),
    [saving, setSaving] = useState(false),
    [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => {
    if (!record.id && !form.personalkategorieId && externalCategory) {
      setForm((current) => ({ ...current, personalkategorieId: externalCategory.id }));
    }
  }, [record.id, form.personalkategorieId, externalCategory]);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial());
  const requestClose = useUnsavedChanges(dirty, onClose);
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const body = {
        ...form,
        mitarbeitertyp: categories.find((item) => item.id === form.personalkategorieId)?.mitarbeitertyp,
        pensumProzent: Number(form.pensumProzent),
        ...(record.id ? { version: record.version } : {}),
      };
      await api(record.id ? `/employees/${record.id}` : "/employees", {
        method: record.id ? "PATCH" : "POST",
        ...(record.id
          ? {}
          : { headers: { "Idempotency-Key": newIdempotencyKey("employees") } }),
        body: JSON.stringify(body),
        successMessage: "Teammitglied gespeichert.",
      });
      onSaved();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    setSaving(true);
    setError("");
    try {
      await api(account ? `/users/${account.id}` : `/employees/${record.id}?version=${record.version}`, {
        method: "DELETE",
        successMessage: account ? "Konto gelöscht." : "Teammitglied gelöscht.",
      });
      onSaved();
    } catch (requestError) {
      setError(requestError.message);
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };
  const reactivate = async () => {
    setSaving(true);
    setError("");
    try {
      await api(`/employees/${record.id}`, {
        method: "PATCH",
        body: JSON.stringify({ version: record.version, aktiv: true, inaktivAb: null }),
        successMessage: "Teammitglied reaktiviert.",
      });
      onSaved();
    } catch (requestError) {
      setError(requestError.status === 409
        ? `Reaktivierung nicht möglich: ${requestError.message} Laden Sie die Teamliste neu und prüfen Sie das Kürzel.`
        : requestError.message);
    } finally {
      setSaving(false);
    }
  };
  const toggleDay = (day) =>
    setForm((current) => ({
      ...current,
      freieWochentage: current.freieWochentage.includes(day)
        ? current.freieWochentage.filter((item) => item !== day)
        : [...current.freieWochentage, day].sort(),
    }));
  if (readOnly) {
    const category = categories.find((item) => item.id === record.personalkategorieId);
    return (
      <Dialog title="Teammitglied" onClose={onClose}>
        <p className="dialog-subtitle">Kürzel {record.kuerzel}</p>
        <dl className="team-form">
          <dt>Name</dt>
          <dd>{record.name}</dd>
          <dt>Rolle</dt>
          <dd>{category?.name || record.personalkategorieId}</dd>
          <dt>Pensum</dt>
          <dd>{record.pensumProzent}%</dd>
          {record.freieWochentage?.length > 0 && (
            <>
              <dt>Freie Wochentage</dt>
              <dd>{record.freieWochentage.map((day) => TEAM_DAYS[day]).join(", ")}</dd>
            </>
          )}
        </dl>
        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={onClose}>Schliessen</Button>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      title={record.id ? "Teammitglied bearbeiten" : "Neues Teammitglied"}
      onClose={requestClose}
    >
      <p className="dialog-subtitle">
        {record.id
          ? `Kürzel ${record.kuerzel} — bleibt als ID unverändert.`
          : "Erscheint sofort in Planung, Kapazität und Jahresansicht."}
      </p>
      <form className="team-form" onSubmit={submit}>
        <Field id="team-name" label="Name">
          <input
            required
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            placeholder="z. B. Michael Frei"
          />
        </Field>
        {!record.id && (
          <Field id="team-code" label="Kürzel">
            <input
              required
              maxLength="6"
              value={form.kuerzel}
              onChange={(event) =>
                setForm({ ...form, kuerzel: event.target.value })
              }
              placeholder="z. B. MF"
            />
          </Field>
        )}
        <Field id="team-role" label="Rolle">
          <select
            required
            disabled={!record.id}
            value={form.personalkategorieId}
            onChange={(event) =>
              setForm({ ...form, personalkategorieId: event.target.value })
            }
          >
            <option value="">Bitte wählen</option>
            {allowedCategories
              .map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </Field>
        <Field id="team-pensum" label="Pensum (%)">
          <input
            required
            type="number"
            min="10"
            max="100"
            step="10"
            value={form.pensumProzent}
            onChange={(event) =>
              setForm({ ...form, pensumProzent: event.target.value })
            }
          />
        </Field>
        {Number(form.pensumProzent) < 100 && (
          <fieldset className="team-days">
            <legend>Freie(r) Wochentag(e) bei Teilzeit</legend>
            <div>
              {TEAM_DAYS.map((label, day) => (
                <label key={label}>
                  <input
                    type="checkbox"
                    checked={form.freieWochentage.includes(day)}
                    onChange={() => toggleDay(day)}
                  />{" "}
                  {label}
                </label>
              ))}
            </div>
            <small>
              An diesen Tagen wird die verfügbare Kapazität auf null gesetzt —
              Planungseinträge lassen sich trotzdem erfassen.
            </small>
          </fieldset>
        )}
        {error && <Notice tone="error">Aktion fehlgeschlagen: {error}</Notice>}
        <div className="form-actions">
          {record.id && record.aktiv === false && !confirmDelete && (
            <Button type="button" onClick={reactivate} disabled={saving}>
              {saving ? "Wird reaktiviert …" : "Reaktivieren"}
            </Button>
          )}
          {record.id && record.aktiv !== false && !confirmDelete && (
            <Button
              type="button"
              variant="danger"
              onClick={() => setConfirmDelete(true)}
            >
              {account ? "Konto löschen" : "Teammitglied löschen"}
            </Button>
          )}
          {confirmDelete && (
            <>
              <span className="delete-question">
                {account
                  ? `Das Konto von ${record.name} wirklich endgültig löschen? Der Zugang wird sofort gesperrt; historische Zuordnungen bleiben erhalten.`
                  : "Dieses Teammitglied wirklich löschen? Historische Zuordnungen bleiben erhalten."}
              </span>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirmDelete(false)}
              >
                Doch nicht
              </Button>
              <Button type="button" onClick={remove} disabled={saving}>
                Endgültig löschen
              </Button>
            </>
          )}
          <Button type="button" variant="secondary" onClick={requestClose}>
            Abbrechen
          </Button>
          <Button disabled={saving || (record.id && !dirty)}>
            {saving
              ? "Wird gespeichert …"
              : record.id
                ? "Speichern"
                : "Teammitglied hinzufügen"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
export function ResourceDialog({
  spec,
  record,
  user,
  navigate,
  onClose,
  onSaved,
}) {
  const initialForm = () =>
    Object.fromEntries(
      spec.fields.map(([key, , type]) => [
        key,
        record[key] ?? (type === "checkbox" ? true : ""),
      ]),
    );
  const [form, setForm] = useState(initialForm),
    [error, setError] = useState(""),
    [saving, setSaving] = useState(false),
    [confirmDelete, setConfirmDelete] = useState(false),
    [confirmHardDelete, setConfirmHardDelete] = useState(false),
    [childDirty, setChildDirty] = useState({}),
    createKey = useRef(newIdempotencyKey(spec.path.slice(1)));
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm());
  const nestedDirty = Object.values(childDirty).some(Boolean),
    requestClose = useUnsavedChanges(dirty || nestedDirty, onClose);
  const reportChildDirty = useCallback(
    (key, value) =>
      setChildDirty((current) =>
        current[key] === value ? current : { ...current, [key]: value },
      ),
    [],
  );
  const employees = useData("/employees?limit=100"),
    mandates = useData("/mandates?limit=100");
  const personnel = useData("/reference-data/personalkategorien?limit=100"),
    confirmationTypes = useData("/reference-data/bestaetigungsarten?limit=100"),
    confirmationStatuses = useData(
      "/reference-data/bestaetigungsstatus?limit=100",
    );
  const references = {
    personalkategorien: personnel.data?.items || [],
    bestaetigungsarten: confirmationTypes.data?.items || [],
    bestaetigungsstatus: confirmationStatuses.data?.items || [],
  };
  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError("");
    const body = Object.fromEntries(
      spec.fields
        .map(([key, , type]) => [
          key,
          type === "number" ? Number(form[key]) : form[key],
        ])
        .filter(([, v]) => v !== ""),
    );
    if (spec.path === "/offers" && !record.id) body.entscheid = body.datumVersand ? "versendet" : "offen";
    if (record.id) body.version = record.version;
    try {
      await api(record.id ? `${spec.path}/${record.id}` : spec.path, {
        method: record.id ? "PATCH" : "POST",
        ...(record.id
          ? {}
          : { headers: { "Idempotency-Key": createKey.current }, retry: 1 }),
        body: JSON.stringify(body),
        successMessage: `${spec.title} gespeichert.`,
      });
      onSaved();
    } catch (x) {
      setError(x.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    setSaving(true);
    setError("");
    try {
      await api(`${spec.path}/${record.id}?version=${record.version}`, {
        method: "DELETE",
        successMessage: ["/mandates", "/employees"].includes(spec.path)
          ? `${spec.title} archiviert.`
          : `${spec.title} gelöscht.`,
      });
      onSaved();
    } catch (x) {
      setError(x.message);
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };
  const restore = async () => {
    setSaving(true);
    setError("");
    try {
      await api(`/mandates/${record.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "aktiv", version: record.version }),
        successMessage: "Projekt wiederhergestellt.",
      });
      onSaved();
    } catch (x) {
      setError(x.message);
    } finally {
      setSaving(false);
    }
  };
  const hardDelete = async () => {
    setSaving(true);
    setError("");
    try {
      await api(`${spec.path}/${record.id}?version=${record.version}&hard=1`, {
        method: "DELETE",
        successMessage: "Unreferenzierter Datensatz endgültig gelöscht.",
      });
      onSaved();
    } catch (x) {
      setError(x.message);
      setConfirmHardDelete(false);
    } finally {
      setSaving(false);
    }
  };
  const choices = (type, options) =>
    type === "employee"
      ? (employees.data?.items || []).map((x) => ({
          value: x.id,
          label: x.name,
        }))
      : type === "mandate"
        ? (mandates.data?.items || []).map((x) => ({
            value: x.id,
            label: x.name,
          }))
        : type === "reference"
          ? (references[options] || [])
              .filter((x) => x.aktiv !== false)
              .map((x) => ({ value: x.id, label: x.name }))
          : options?.map((x) => ({ value: x, label: x }));
  const visibleFields =
    spec.path === "/offers" && !record.id
      ? spec.fields.filter(
          ([key]) =>
            !["entscheid", "entscheidDatum", "entscheidKommentar"].includes(
              key,
            ),
        )
      : spec.path === "/offers"
        ? spec.fields.filter(
            ([key]) =>
              !["entscheid", "entscheidDatum", "entscheidKommentar"].includes(
                key,
              ),
          )
        : spec.fields;
  return (
    <Dialog
      title={record.id ? `${spec.title}: Details` : spec.create}
      onClose={requestClose}
    >
      <form className="domain-form" onSubmit={submit}>
        {(dirty || nestedDirty) && <Notice>Ungespeicherte Änderungen</Notice>}
        {visibleFields.map(([key, label, type = "text", required, options]) => {
          const values = choices(type, options);
          const decisionRequired =
            spec.path === "/offers" &&
            ["entscheidDatum", "entscheidKommentar"].includes(key) &&
            form.entscheid !== "offen";
          return (
            <Field key={key} id={`field-${key}`} label={label}>
              {values ? (
                <select
                  required={!!required}
                  disabled={
                    spec.path === "/offers" &&
                    !!record.mandatId &&
                    [
                      "entscheid",
                      "entscheidDatum",
                      "entscheidKommentar",
                    ].includes(key)
                  }
                  value={form[key]}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      [key]: e.target.value,
                      ...(key === "entscheid" && e.target.value === "offen"
                        ? { entscheidDatum: "", entscheidKommentar: "" }
                        : {}),
                    })
                  }
                >
                  <option value="">Bitte wählen</option>
                  {values.map((x) => (
                    <option key={x.value} value={x.value}>
                      {x.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={type}
                  maxLength={key === "empfohlenDurchKanal" ? 200 : undefined}
                  disabled={
                    spec.path === "/offers" &&
                    !!record.mandatId &&
                    [
                      "entscheid",
                      "entscheidDatum",
                      "entscheidKommentar",
                    ].includes(key)
                  }
                  required={!!required || decisionRequired}
                  checked={type === "checkbox" ? form[key] : undefined}
                  value={type === "checkbox" ? undefined : form[key]}
                  min={type === "number" ? 0 : undefined}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      [key]:
                        type === "checkbox" ? e.target.checked : e.target.value,
                    })
                  }
                />
              )}
            </Field>
          );
        })}
        {error && (
          <Notice tone="error">
            <strong>
              {error.includes("changed") || error.includes("Versions")
                ? "Versionskonflikt. Neu laden und erneut versuchen."
                : "Aktion fehlgeschlagen:"}
            </strong>{" "}
            {error}{" "}
            <Button type="button" variant="secondary" onClick={submit}>
              Erneut versuchen
            </Button>
          </Notice>
        )}
        <div className="form-actions">
          {record.id &&
            !confirmDelete &&
            !(spec.path === "/mandates" && record.status === "archiviert") && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirmDelete(true)}
              >
                {["/mandates", "/employees"].includes(spec.path)
                  ? "Archivieren"
                  : "Löschen"}
              </Button>
            )}
          {confirmDelete && (
            <>
              <span className="delete-question">
                {["/mandates", "/employees"].includes(spec.path)
                  ? "Archivieren? Historische Referenzen bleiben erhalten."
                  : "Eintrag wirklich löschen?"}
              </span>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirmDelete(false)}
              >
                Nein
              </Button>
              <Button type="button" onClick={remove} disabled={saving}>
                {["/mandates", "/employees"].includes(spec.path)
                  ? "Ja, archivieren"
                  : "Ja, löschen"}
              </Button>
            </>
          )}
          {spec.path === "/mandates" && record.status === "archiviert" && (
            <Button
              type="button"
              variant="secondary"
              onClick={restore}
              disabled={saving}
            >
              Wiederherstellen
            </Button>
          )}
          {["/mandates", "/employees"].includes(spec.path) &&
            record.id &&
            user?.berechtigungsstufe === "administrator" &&
            !confirmHardDelete && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirmHardDelete(true)}
              >
                Endgültig löschen
              </Button>
            )}
          {confirmHardDelete && (
            <>
              <span className="delete-question">
                Endgültig löschen? Nur unreferenzierte Datensätze können
                gelöscht werden. Dies kann nicht rückgängig gemacht werden.
              </span>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirmHardDelete(false)}
              >
                Nein
              </Button>
              <Button type="button" onClick={hardDelete} disabled={saving}>
                Ja, endgültig löschen
              </Button>
            </>
          )}
          <Button type="button" variant="secondary" onClick={requestClose}>
            Abbrechen
          </Button>
          <Button disabled={saving || (!dirty && record.id)}>
            {saving
              ? "Wird gespeichert …"
              : dirty
                ? "Speichern"
                : "Gespeichert"}
          </Button>
        </div>
      </form>
      {record.id && spec.path === "/mandates" && (
        <ProjectDetails project={record} navigate={navigate} onDirtyChange={reportChildDirty} />
      )}{" "}
      {record.id && spec.path === "/offers" && (
        <OfferWorkflow
          offer={record}
          navigate={navigate}
          onChanged={onSaved}
          onDirtyChange={reportChildDirty}
        />
      )}{" "}
      {record.id && spec.path === "/employees" && (
        <>
          <EmployeeRelationships employeeId={record.id} navigate={navigate} />
          <Absences employeeId={record.id} onDirtyChange={reportChildDirty} />
        </>
      )}
    </Dialog>
  );
}

export function ProjectComment({ project, onDirtyChange }) {
  const [comment, setComment] = useState(project.kommentar || "");
  const [savedComment, setSavedComment] = useState(project.kommentar || "");
  const [version, setVersion] = useState(project.version);
  const [request, setRequest] = useState({ status: "idle", message: "" });
  const dirty = comment !== savedComment;
  useEffect(() => { onDirtyChange?.("project-comment", dirty); return () => onDirtyChange?.("project-comment", false); }, [dirty, onDirtyChange]);
  const save = async (event) => {
    event?.preventDefault();
    if (comment.length > 2000) { setRequest({ status: "validation", message: "Der Kommentar darf höchstens 2000 Zeichen lang sein." }); return; }
    setRequest({ status: "saving", message: "" });
    try {
      const saved = await api(`/mandates/${project.id}`, { method: "PATCH", body: JSON.stringify({ kommentar: comment, version }), successMessage: "Kommentar gespeichert." });
      setSavedComment(comment); setVersion(saved?.version ?? version + 1); setRequest({ status: "saved", message: "Kommentar gespeichert." });
    } catch (error) { setRequest({ status: error.status === 409 ? "conflict" : error.status === 403 ? "forbidden" : "error", message: error.message }); }
  };
  return <section className="project-comment project-detail-section"><h3>Kommentar zum Projekt</h3><form onSubmit={save}><label htmlFor="project-comment">Kommentar zum Projekt</label><textarea id="project-comment" placeholder="Notizen, Besonderheiten oder wichtige Hinweise zum Projekt …" value={comment} onChange={(event) => { setComment(event.target.value); setRequest({ status: "idle", message: "" }); }} maxLength="2001" />
    {request.status === "saved" && <p className="project-save-feedback">Kommentar gespeichert.</p>}
    {["conflict", "forbidden", "validation", "error"].includes(request.status) && <Notice tone="error"><strong>{request.status === "conflict" ? "Versionskonflikt." : request.status === "forbidden" ? "Keine Berechtigung." : request.status === "validation" ? "Kommentar ungültig." : "Kommentar konnte nicht gespeichert werden."}</strong> {request.message} {request.status !== "validation" && request.status !== "forbidden" && <Button type="button" variant="secondary" onClick={save}>Erneut versuchen</Button>}</Notice>}
    <Button disabled={!dirty || request.status === "saving"}>{request.status === "saving" ? "Wird gespeichert …" : "Kommentar speichern"}</Button></form></section>;
}

export function ProjectDetails({ project, navigate, onDirtyChange, planningYear, showOverviewHeading = true }) {
  const planning = useData("/planning-entries?limit=1000"),
    employees = useData("/employees?limit=1000"),
    phases = useData("/reference-data/phasen?limit=100"),
    dispositions = useData("/reference-data/dispositionsstatus?limit=100");
  const entries = (planning.data?.items || []).filter(
      (item) => item.mandatId === project.id,
    ),
    snapshot = projectSnapshots(entries, new Date(), {
      phases: phases.data?.items || [],
      dispositions: dispositions.data?.items || [],
    }, planningYear)[project.id] || {
      plannedHours: 0,
      phases: [],
      nextDate: null,
      disposition: "nfx",
    },
    budget = projectBudget(project.budget);
  return (
    <section className="project-workflow" data-budget={budget} data-planned={snapshot.plannedHours}>
      {showOverviewHeading && <h3 className="project-workflow-heading">Projektübersicht</h3>}
      <ProjectPlanningPanel projectId={project.id} planning={planning} employees={employees} phases={phases} dispositions={dispositions} planningYear={planningYear} />
      <ProjectComment project={project} onDirtyChange={onDirtyChange} />
      <ProjectConfirmations project={project} navigate={navigate} />
      <Milestones mandateId={project.id} onDirtyChange={onDirtyChange} />
      <Documents mandateId={project.id} onDirtyChange={onDirtyChange} />
    </section>
  );
}

function ChildRecords({
  title,
  path,
  parentKey,
  parentId,
  fields,
  columns,
  empty,
  editable = false,
  className = "",
  onDirtyChange,
}) {
  const initial = () =>
    Object.fromEntries(
      fields.map((x) => [x[0], x[2] === "checkbox" ? false : ""]),
    );
  const records = useData(`${path}?limit=100`),
    [form, setForm] = useState(initial),
    [editing, setEditing] = useState(null),
    [deleting, setDeleting] = useState(null),
    [error, setError] = useState(""),
    baseline = useRef(initial()),
    createKey = useRef(newIdempotencyKey(path.slice(1)));
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline.current);
  useEffect(() => {
    onDirtyChange?.(path, dirty);
    return () => onDirtyChange?.(path, false);
  }, [dirty, onDirtyChange, path]);
  const rows = (records.data?.items || []).filter(
    (x) => x[parentKey] === parentId,
  );
  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (path === "/document-links") {
      try {
        if (new URL(form.sharePointLink).protocol !== "https:") throw new Error();
      } catch {
        setError("Der SharePoint-Link muss eine gültige HTTPS-Adresse sein.");
        return;
      }
    }
    const body = {
      ...form,
      [parentKey]: parentId,
      ...(editing ? { version: editing.version } : {}),
    };
    for (const [key, , type] of fields)
      if (type === "number") body[key] = Number(body[key]);
    try {
      await api(editing ? `${path}/${editing.id}` : path, {
        method: editing ? "PATCH" : "POST",
        ...(editing
          ? {}
          : { headers: { "Idempotency-Key": createKey.current }, retry: 1 }),
        body: JSON.stringify(body),
        successMessage: `${title} gespeichert.`,
      });
      baseline.current = initial();
      setForm(baseline.current);
      setEditing(null);
      createKey.current = newIdempotencyKey(path.slice(1));
      records.reload();
    } catch (x) {
      setError(x.message);
    }
  };
  const edit = (row) => {
    const values = Object.fromEntries(
      fields.map(([key, , type]) => [
        key,
        row[key] ?? (type === "checkbox" ? false : ""),
      ]),
    );
    baseline.current = values;
    setEditing(row);
    setDeleting(null);
    setError("");
    setForm(values);
  };
  const remove = async (row) => {
    try {
      await api(`${path}/${row.id}?version=${row.version}`, {
        method: "DELETE",
        successMessage: `${title} gelöscht.`,
      });
      setDeleting(null);
      records.reload();
    } catch (x) {
      setError(x.message);
    }
  };
  const actions = {
    key: "actions",
    label: "",
    render: (r) => (
      <span className="table-actions">
        {editable && (
          <Button variant="secondary" onClick={() => edit(r)}>
            Bearbeiten
          </Button>
        )}{" "}
        {deleting?.id === r.id ? (
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Nein
            </Button>{" "}
            <Button onClick={() => remove(r)}>Ja, löschen</Button>
          </>
        ) : (
          <Button variant="secondary" onClick={() => setDeleting(r)}>
            Löschen
          </Button>
        )}
      </span>
    ),
  };
  return (
    <section className={`child-records ${className}`.trim()}>
      <h3>{title}</h3>
      <LoadState state={records}>
        <DataTable columns={[...columns, actions]} rows={rows} empty={empty} />
      </LoadState>
      {deleting && (
        <Notice tone="error">
          „{deleting.bezeichnung || deleting.dokumentname || deleting.grund}“
          wirklich löschen?
        </Notice>
      )}
      <form className="child-form" onSubmit={save}>
        {fields.map(([key, label, type = "text", options]) => (
          <Field key={key} id={`${path}-${key}`} label={label}>
            {type === "select" ? (
              <select
                required
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              >
                <option value="">Bitte wählen</option>
                {options.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            ) : (
              <input
                required={type !== "checkbox"}
                type={type === "checkbox" ? "checkbox" : type}
                checked={type === "checkbox" ? form[key] : undefined}
                value={type === "checkbox" ? undefined : form[key]}
                onChange={(e) =>
                  setForm({
                    ...form,
                    [key]:
                      type === "checkbox" ? e.target.checked : e.target.value,
                  })
                }
              />
            )}
          </Field>
        ))}
        {editing && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              baseline.current = initial();
              setEditing(null);
              setForm(baseline.current);
            }}
          >
            Abbrechen
          </Button>
        )}
        <Button>{editing ? "Änderungen speichern" : "+ Hinzufügen"}</Button>
      </form>
      {error && <Notice tone="error">Aktion fehlgeschlagen: {error}</Notice>}
    </section>
  );
}
function Documents({ mandateId, onDirtyChange }) {
  return (
    <ChildRecords
      onDirtyChange={onDirtyChange}
      className="project-documents project-detail-section"
      title="Dokumente (SharePoint)"
      path="/document-links"
      parentKey="mandatId"
      parentId={mandateId}
      fields={[
        ["dokumentname", "Dokumentname"],
        ["sharePointLink", "SharePoint-Link", "url"],
      ]}
      columns={[
        { key: "dokumentname", label: "Dokumentname" },
        {
          key: "sharePointLink",
          label: "",
          render: (r) => (
            <a
              href={r.sharePointLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              SharePoint ↗
            </a>
          ),
        },
      ]}
      empty="Noch keine Dokumente verlinkt."
    />
  );
}
function Milestones({ mandateId, onDirtyChange }) {
  return (
    <ChildRecords
      onDirtyChange={onDirtyChange}
      className="project-milestones project-detail-section"
      title="Zeitplan (Deadlines)"
      path="/milestones"
      parentKey="mandatId"
      parentId={mandateId}
      fields={[
        ["bezeichnung", "Bezeichnung"],
        ["datum", "Datum", "date"],
        ["kritisch", "kritische Deadline", "checkbox"],
      ]}
      columns={[
        { key: "bezeichnung", label: "Bezeichnung" },
        { key: "datum", label: "Datum", render: (r) => dateCH(r.datum) },
        {
          key: "kritisch",
          label: "",
          render: (r) => (r.kritisch ? <Badge tone="red">kritisch</Badge> : ""),
        },
      ]}
      empty="Noch keine Zeitplan-Einträge."
      editable
    />
  );
}
function EmployeeRelationships({ employeeId, navigate }) {
  const planning = useData("/planning-entries?limit=1000"),
    mandates = useData("/mandates?limit=1000"),
    today = new Date().toISOString().slice(0, 10);
  const projects = Object.fromEntries(
    (mandates.data?.items || []).map((x) => [x.id, x]),
  );
  const rows = (planning.data?.items || [])
    .filter((x) => x.mitarbeiterId === employeeId)
    .sort((a, b) => b.datum.localeCompare(a.datum));
  const columns = [
    { key: "datum", label: "Datum", render: (r) => dateCH(r.datum) },
    {
      key: "mandatId",
      label: "Mandat",
      render: (r) => (
        <button
          className="table-link"
          onClick={() => navigate(`/projekte/${r.mandatId}`)}
        >
          {projects[r.mandatId]?.name || "Historische Referenz"}
        </button>
      ),
    },
    { key: "stunden", label: "Planung", render: (r) => `${r.stunden} h` },
    {
      key: "status",
      label: "Bezug",
      render: (r) => (
        <Badge tone={r.datum >= today ? "navy" : undefined}>
          {r.datum >= today ? "Aktuell / geplant" : "Historisch"}
        </Badge>
      ),
    },
  ];
  return (
    <section className="child-records">
      <h3>Mandate und Planung (aktuell und historisch)</h3>
      <LoadState state={planning}>
        <DataTable
          columns={columns}
          rows={rows}
          empty="Noch keine Mandats- oder Planungsbezüge vorhanden."
        />
      </LoadState>
      <Button
        variant="secondary"
        onClick={() => navigate(`/planung?mitarbeiterId=${employeeId}`)}
      >
        Planung dieses Teammitglieds öffnen
      </Button>
    </section>
  );
}
function Absences({ employeeId, onDirtyChange }) {
  const categories = useData(
    "/reference-data/abwesenheitskategorien?limit=100",
  );
  return (
    <LoadState state={categories}>
      <ChildRecords
        onDirtyChange={onDirtyChange}
        title="Abwesenheitsverlauf"
        path="/absences"
        parentKey="mitarbeiterId"
        parentId={employeeId}
        fields={[
          ["von", "Von", "date"],
          ["bis", "Bis", "date"],
          ["stunden", "Stunden", "number"],
          [
            "grund",
            "Kategorie",
            "select",
            (categories.data?.items || []).map((x) => x.name),
          ],
          ["kommentar", "Kommentar", "text"],
        ]}
        columns={[
          { key: "von", label: "Von", render: (r) => dateCH(r.von) },
          { key: "bis", label: "Bis", render: (r) => dateCH(r.bis) },
          { key: "stunden", label: "Stunden" },
          { key: "grund", label: "Kategorie" },
          { key: "kommentar", label: "Kommentar" },
        ]}
        empty="Noch keine Abwesenheiten erfasst."
        editable
      />
    </LoadState>
  );
}
export function OfferWorkflow({ offer, navigate, onChanged, onDirtyChange }) {
  const followUps = useData("/offer-follow-ups?limit=100"),
    employees = useData("/employees?limit=100"),
    [form, setForm] = useState({ datum: "", notiz: "" }),
    [decision, setDecision] = useState(null),
    [decisionDate, setDecisionDate] = useState(""),
    [decisionComment, setDecisionComment] = useState(""),
    [state, setState] = useState({ status: "idle" }),
    [confirmingDelete, setConfirmingDelete] = useState(null),
    [deletingId, setDeletingId] = useState(null),
    createKey = useRef(newIdempotencyKey("offer-follow-up"));
  const dirty =
    Boolean(form.datum || form.notiz) ||
    ["saving-followup", "failure", "forbidden"].includes(state.status);
  useEffect(() => {
    onDirtyChange?.("offer-follow-up", dirty);
    return () => onDirtyChange?.("offer-follow-up", false);
  }, [dirty, onDirtyChange]);
  const rows = (followUps.data?.items || [])
    .filter((x) => x.offerteId === offer.id)
    .sort((a, b) => b.datum.localeCompare(a.datum));
  const add = async (e) => {
    e.preventDefault();
    setState({ status: "saving-followup" });
    try {
      await api("/offer-follow-ups", {
        method: "POST",
        headers: { "Idempotency-Key": createKey.current },
        retry: 1,
        body: JSON.stringify({ ...form, offerteId: offer.id }),
        successMessage: "Follow-up gespeichert.",
      });
      setForm({ datum: "", notiz: "" });
      createKey.current = newIdempotencyKey("offer-follow-up");
      setState({ status: "completed-followup" });
      followUps.reload();
    } catch (error) {
      setState({
        status: error.status === 403 ? "forbidden" : "failure",
        error,
      });
    }
  };
  const removeFollowUp = async (followUp) => {
    setDeletingId(followUp.id);
    setState({ status: "deleting-followup" });
    try {
      await api(`/offer-follow-ups/${followUp.id}?version=${followUp.version}`, {
        method: "DELETE",
        successMessage: "Follow-up gelöscht.",
      });
      setConfirmingDelete(null);
      setState({ status: "deleted-followup" });
      followUps.reload();
    } catch (error) {
      setState({
        status: error.status === 409 ? "conflict" : error.status === 403 ? "forbidden" : "failure",
        error,
      });
    } finally {
      setDeletingId(null);
    }
  };
  const openConversion = () => setState({ status: "conversion-dialog" });
  const decide = async () => {
    const patch = offerStatusPatch(offer, decision, new Date(), decisionDate);
    setState({ status: "deciding" });
    try {
      await api(`/offers/${offer.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          ...patch,
          entscheidKommentar:
            decisionComment || (decision === "gewonnen" ? "Zusage" : "Absage"),
          version: offer.version,
        }),
        successMessage: "Entscheid gespeichert.",
      });
      setDecision(null);
      setDecisionDate("");
      setDecisionComment("");
      setState({ status: decision === "gewonnen" ? "conversion-dialog" : "decision-complete" });
      // Keep this detail view mounted so the newly won offer can be completed
      // with confirmed project master data before the parent reloads it.
      if (decision !== "gewonnen") onChanged();
    } catch (error) {
      setState({
        status: error.status === 403 ? "forbidden" : "failure",
        error,
      });
    }
  };
  const error = ["failure", "forbidden", "conflict", "rollback"].includes(
    state.status,
  );
  return (
    <section className="child-records">
      <h3>Follow-up-Verlauf</h3>
      <LoadState state={followUps}>
        <DataTable
          columns={[
            {
              key: "datum",
              label: "Nachfassdatum",
              render: (r) => dateCH(r.datum),
            },
            { key: "notiz", label: "Notiz" },
            {
              key: "actions",
              label: "Aktionen",
              render: (r) => confirmingDelete === r.id ? (
                <div className="form-actions">
                  <span className="delete-question">
                    Follow-up vom {dateCH(r.datum)} („{r.notiz}“) wirklich löschen? Die Offerte bleibt erhalten.
                  </span>
                  <Button type="button" variant="secondary" onClick={() => setConfirmingDelete(null)} disabled={deletingId === r.id}>Abbrechen</Button>
                  <Button type="button" onClick={() => removeFollowUp(r)} disabled={deletingId === r.id}>
                    {deletingId === r.id ? "Wird gelöscht …" : "Ja, Follow-up löschen"}
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="secondary" onClick={() => setConfirmingDelete(r.id)} disabled={deletingId === r.id} aria-label={`Follow-up vom ${dateCH(r.datum)} löschen`}>
                  Löschen
                </Button>
              ),
            },
          ]}
          rows={rows}
          empty="Noch kein Follow-up erfasst."
        />
      </LoadState>
      {state.status === "saving-followup" && (
        <Notice>Follow-up wird gespeichert …</Notice>
      )}
      {state.status === "completed-followup" && (
        <Notice>Follow-up gespeichert.</Notice>
      )}
      {state.status === "deleted-followup" && <Notice>Follow-up gelöscht.</Notice>}
      {state.status === "conflict" && (
        <Notice tone="error"><strong>Versionskonflikt.</strong> Der Follow-up-Verlauf wurde zwischenzeitlich geändert. Bitte laden Sie ihn neu und versuchen Sie es erneut. <Button type="button" variant="secondary" onClick={followUps.reload}>Neu laden</Button></Notice>
      )}
      {state.status === "forbidden" && (
        <Notice tone="error"><strong>Keine Berechtigung.</strong> Sie dürfen dieses Follow-up nicht löschen.</Notice>
      )}
      {state.status === "failure" && state.error && (
        <Notice tone="error"><strong>Löschen fehlgeschlagen.</strong> {state.error.message}</Notice>
      )}
      {["offen", "versendet"].includes(offerStatus(offer)) && (
        <>
          <form className="child-form" onSubmit={add}>
            <Field id="follow-date" label="Nachfassdatum">
              <input
                required
                type="date"
                value={form.datum}
                onChange={(e) => setForm({ ...form, datum: e.target.value })}
              />
            </Field>
            <Field id="follow-note" label="Notiz zum Follow-up">
              <input
                required
                value={form.notiz}
                onChange={(e) => setForm({ ...form, notiz: e.target.value })}
              />
            </Field>
            <Button disabled={state.status === "saving-followup"}>
              Follow-up hinzufügen
            </Button>
          </form>
          <div className="offer-decision-actions">
            <Button
              onClick={() => {
                setDecision("gewonnen");
                setDecisionDate((value) => value || new Date().toISOString().slice(0, 10));
                setState({ status: "idle" });
              }}
            >
              Als gewonnen markieren
            </Button>
            <Button
              onClick={() => {
                setDecision("abgesagt");
                setDecisionDate((value) => value || new Date().toISOString().slice(0, 10));
                setState({ status: "idle" });
              }}
            >
              Als abgesagt markieren
            </Button>
          </div>
          {decision && (
            <div className="offer-decision-form">
              <Field id="decision-date" label="Entscheiddatum">
                <input
                  required
                  type="date"
                  value={decisionDate}
                  onChange={(event) => {
                    setDecisionDate(event.target.value);
                    setState({ status: "idle" });
                  }}
                />
              </Field>
              <Field
                id="decision-comment"
                label={
                  decision === "abgesagt"
                    ? "Kommentar zur Absage (empfohlen)"
                    : "Kommentar (optional)"
                }
              >
                <textarea
                  value={decisionComment}
                  onChange={(event) => setDecisionComment(event.target.value)}
                />
              </Field>
              <Button onClick={decide} disabled={state.status === "deciding"}>
                Entscheid speichern
              </Button>
            </div>
          )}
        </>
      )}
      {offer.entscheid === "gewonnen" && !offer.mandatId && (
        <Button onClick={openConversion}>
          Einmalig in Projekt umwandeln
        </Button>
      )}
      {state.status === "conversion-dialog" && (
        <Dialog title="Offerte in Projekt umwandeln" onClose={() => setState({ status: "idle" })}>
          <ProjectForm
            project={{ name: offer.firma, uid: offer.uid, mandatsleitungId: offer.verantwortlichId }}
            employees={employees.data?.items || []}
            conversion={{ offerId: offer.id, betragVon: offer.betragVon, betragBis: offer.betragBis }}
            onCancel={() => setState({ status: "idle" })}
            onSaved={(project) => {
              setState({ status: "converted", data: { mandatId: project.id } });
              onChanged();
            }}
          />
        </Dialog>
      )}
      {state.status === "converted" && (
        <Notice>
          <strong>Konvertierung abgeschlossen.</strong> Offerte und
          Audit-Verlauf bleiben erhalten.{" "}
          {state.data?.mandatId && (
            <Button
              variant="secondary"
              onClick={() => navigate(`/projekte/${state.data.mandatId}`)}
            >
              Projekt öffnen
            </Button>
          )}
        </Notice>
      )}
      {offer.mandatId && (
        <Notice>
          Automatisch als Projekt angelegt. Offerte und Verlauf bleiben
          erhalten.{" "}
          <Button
            variant="secondary"
            onClick={() => navigate(`/projekte/${offer.mandatId}`)}
          >
            Projekt öffnen
          </Button>
        </Notice>
      )}
      {error && (
        <Notice tone="error">
          <strong>
            {state.status === "forbidden"
              ? "Kein Zugriff"
              : state.status === "conflict"
                ? "Konflikt"
                : state.status === "rollback"
                  ? "Konvertierung zurückgerollt"
                  : "Speichern fehlgeschlagen"}
            .
          </strong>{" "}
          {state.error?.message}{" "}
          <Button
            variant="secondary"
            onClick={() =>
              state.status === "failure"
                ? setState({ status: "idle" })
                : openConversion()
            }
          >
            Erneut versuchen
          </Button>
        </Notice>
      )}
    </section>
  );
}


function PlanningCalendar({
  period,
  rangePeriod = period,
  employees,
  entries,
  absences,
  holidays,
  employeeId,
  mandates,
  dispositions = [],
  phases = [],
  onCreate,
  onEdit,
  onMove,
}) {
  const [selectedMandate, setSelectedMandate] = useState(null),
    [collapsedEmployees, setCollapsedEmployees] = useState(() => new Set()),
    [employeesWithHistory, setEmployeesWithHistory] = useState(() => new Set()),
    rows = calendarRows({
      period: rangePeriod,
      employees,
      entries,
      absences,
      holidays,
      employeeId,
    }),
    days = calendarDays(period, holidays),
    available = authorizedPlanningMandates(mandates),
    today = new Date().toISOString().slice(0, 10);
  const mandateById = Object.fromEntries(mandates.map((x) => [x.id, x])),
    dispositionById = Object.fromEntries(dispositions.map((x) => [x.id, x]));
  const disposition = (id) => {
    const item = dispositionById[id],
      code = String(item?.code || id || "nfx").toLowerCase();
    return {
      label: item?.name || dispositionPresentation(code),
      tone: code.startsWith("fx")
        ? "fx"
        : ["flex", "nfx", "res"].includes(code)
          ? code
          : "default",
    };
  };
  const initials = (e) =>
    e.kuerzel ||
    e.name
      ?.split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((x) => x[0])
      .join("")
      .toUpperCase() ||
    "?";
  const drop = (event, mitarbeiterId, datum) => {
    event.preventDefault();
    event.currentTarget.classList.remove("dragover");
    const entryId = event.dataTransfer.getData("text/planning-entry"),
      mandatId = event.dataTransfer.getData("text/mandate");
    if (entryId) {
      const entry = entries.find((item) => item.id === entryId);
      if (entry) onMove(entry, mitarbeiterId, datum);
    } else if (available.some((item) => item.id === mandatId))
      onCreate(mitarbeiterId, datum, mandatId);
  };
  const assign = (mitarbeiterId, datum) => {
    onCreate(mitarbeiterId, datum, selectedMandate || "");
    if (selectedMandate) setSelectedMandate(null);
  };
  const toggleEmployee = (employeeId) => setCollapsedEmployees((current) => {
    const next = new Set(current);
    next.has(employeeId) ? next.delete(employeeId) : next.add(employeeId);
    return next;
  });
  const toggleMonthHistory = (employeeId) => setEmployeesWithHistory((current) => {
    const next = new Set(current),
      showing = !next.has(employeeId);
    showing ? next.add(employeeId) : next.delete(employeeId);
    return next;
  });
  const chip = (entry) => {
    const project = mandateById[entry.mandatId],
      status = disposition(entry.dispositionsstatusId),
      name = project?.name || "Nicht verfügbares Projekt",
      capacity = /abwesenheit|intern/i.test(name),
      cues = [
        `${entry.stunden} h`,
        entry.halbtag,
        entry.inhouseExtern,
        entry.kommentar && "Kommentar",
      ].filter(Boolean);
    return (
      <button
        draggable
        key={entry.id}
        data-capacity-effect={
          capacity ? "reduces-availability" : "productive-load"
        }
        className={`planning-chip disposition-${status.tone} ${capacity ? "capacity-reduction" : ""}`}
        onClick={() => onEdit(entry)}
        onDragStart={(event) => {
          event.stopPropagation();
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/planning-entry", entry.id);
        }}
        aria-label={`${name}; ${cues.join("; ")}; Disposition ${status.label}. Aktivieren zum Bearbeiten oder Verschieben per Datum.`}
      >
        <strong>{name}</strong>
        <span className="planning-chip-meta">
          {entry.stunden} h · {status.label}
        </span>
        <span className="planning-chip-cues">
          {entry.halbtag && <span>◐ {entry.halbtag}</span>}
          {entry.inhouseExtern && (
            <span>
              {entry.inhouseExtern === "Extern" ? "⌖" : "⌂"}{" "}
              {entry.inhouseExtern}
            </span>
          )}
          {entry.kommentar && <span title={entry.kommentar}>● Kommentar</span>}
        </span>
      </button>
    );
  };
  const dayCell = (day, row, compact = false) => {
    if (!day) return <div className="planning-month-day outside-month" aria-hidden="true" />;
    const current = day.datum === today;
    const visibleEntries = compact ? day.entries.slice(0, 2) : day.entries;
    return (
      <div
        key={day.datum}
        data-date={day.datum}
        aria-label={`${dateCH(day.datum)}${day.holiday ? `, Feiertag ${day.holiday.name}` : ""}${day.weekend ? ", Wochenende" : ""}${current ? ", heute" : ""}`}
        className={`planning-day planning-dropzone ${compact ? "planning-month-day" : ""} ${day.weekend ? "weekend" : ""} ${day.holiday ? "holiday" : ""} ${day.absent ? "absent" : ""} ${current ? "today" : ""}`}
        onDragOver={(event) => { event.preventDefault(); event.currentTarget.classList.add("dragover"); }}
        onDragLeave={(event) => event.currentTarget.classList.remove("dragover")}
        onDrop={(event) => drop(event, row.employee.id, day.datum)}
      >
        {compact && <div className="planning-month-date"><strong>{new Date(`${day.datum}T00:00:00Z`).getUTCDate()}</strong>{current && <span>Heute</span>}{day.holiday && <span title={day.holiday.name}>{day.holiday.name}</span>}</div>}
        <button className="planning-cell-add" aria-label={`Planungseintrag für ${row.employee.name} am ${dateCH(day.datum)} hinzufügen`} onClick={() => assign(row.employee.id, day.datum)}><span aria-hidden="true">+</span></button>
        {day.absent && <small className="planning-absence">∕ Abwesend</small>}
        {visibleEntries.map(chip)}
        {compact && day.entries.length > 2 && (
          <details className="planning-month-more">
            <summary aria-label={`${day.entries.length - 2} weitere Planungseinträge am ${dateCH(day.datum)} anzeigen`}>+{day.entries.length - 2} weitere</summary>
            <div className="planning-month-more-panel">{day.entries.slice(2).map(chip)}</div>
          </details>
        )}
      </div>
    );
  };
  if (period.view === "Monat") {
    const weeks = monthCalendarWeeks(period, holidays),
      historyStart = `${fiscalYearStartFor(period.from)}-10-01`,
      historyWeeks = fiscalWeeks({ from: historyStart, to: period.to }).map((week) => ({
        ...week,
        days: Array.from({ length: 7 }, (_, index) => {
          const date = new Date(`${week.from}T00:00:00Z`);
          date.setUTCDate(date.getUTCDate() + index);
          const datum = date.toISOString().slice(0, 10);
          return datum >= historyStart && datum <= period.to ? { datum } : null;
        }),
      })),
      monthWeekKeys = new Set(weeks.map((week) => `${week.isoYear}-${week.label}`));
    return (
      <div className="planning-week-layout">
        <div className="planning-month-calendar" role="region" aria-label="Monatsansicht Planungskalender" tabIndex="0">
          {rows.map(row => {
            const collapsed = collapsedEmployees.has(row.employee.id),
              showHistory = employeesWithHistory.has(row.employee.id),
              visibleWeeks = showHistory ? historyWeeks : weeks;
            return <section className={`planning-month-person ${collapsed ? "collapsed" : ""}`} key={row.employee.id} aria-labelledby={`month-employee-${row.employee.id}`}>
              <header style={{ "--employee-accent": employeeColor(row.employee.id).background }}>
                <button type="button" className="planning-month-collapse" aria-expanded={!collapsed} aria-controls={`month-table-${row.employee.id}`} aria-label={`${row.employee.name}: Tabelle ${collapsed ? "aufklappen" : "einklappen"}`} onClick={() => toggleEmployee(row.employee.id)}>{collapsed ? "+" : "−"}</button>
                <span className="planning-avatar" aria-hidden="true" style={{ backgroundColor: employeeColor(row.employee.id).background, color: employeeColor(row.employee.id).foreground }}>{initials(row.employee)}</span>
                <strong id={`month-employee-${row.employee.id}`}>{row.employee.name}</strong><span>{entries.filter(item => item.mitarbeiterId === row.employee.id && item.datum >= period.from && item.datum <= period.to).reduce((sum, item) => sum + item.stunden, 0)} h in diesem Monat</span>
              </header>
              {!collapsed && <div id={`month-table-${row.employee.id}`} className="planning-month-table">
                <div className="planning-month-weekdays" aria-hidden="true"><span>KW</span>{["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"].map(name => <strong key={name}>{name}</strong>)}</div>
                <div className="planning-month-weeks">
              {visibleWeeks.map(week => (
                <div className="planning-month-week" data-month-start={monthWeekKeys.has(`${week.isoYear}-${week.label}`) ? "true" : undefined} key={`${week.isoYear}-${week.label}`}>
                  <div className="planning-month-week-number" aria-label={`${week.label}, ${week.isoYear}`}>{week.label}<small>{week.isoYear}</small></div>
                  {week.days.map((day, index) => <Fragment key={day?.datum || `empty-${index}`}>{dayCell(day && row.days.find(item => item.datum === day.datum), row, true)}</Fragment>)}
                </div>
              ))}
                </div>
                <div className="planning-month-history"><button type="button" aria-expanded={showHistory} onClick={() => toggleMonthHistory(row.employee.id)}>{showHistory ? "Vorige Wochen ausblenden" : "Vorige Wochen anzeigen"}</button></div>
              </div>}
            </section>;
          })}
          {!rows.length && <div className="planning-calendar-empty">Keine Teammitglieder für diesen Zeitraum vorhanden.</div>}
        </div>
        <PlanningMandateSidebar mandates={available} employees={employees} phases={phases} selectedMandate={selectedMandate} onSelect={setSelectedMandate} />
      </div>
    );
  }
  return (
    <div className="planning-week-layout">
      <div
        className={`planning-calendar planning-calendar-${period.view.toLowerCase()}`}
        style={{ "--calendar-days": days.length, "--calendar-min-width": `${170 + days.length * 108}px` }}
        role="region"
        aria-label={`${period.view}ansicht Planungskalender`}
        tabIndex="0"
      >
        <div className="planning-calendar-row planning-calendar-head">
          <div className="planning-person planning-person-heading">
            Teammitglied
          </div>
          {days.map((day) => {
            const current = day.datum === today;
            return (
              <div
                key={day.datum}
                data-date={day.datum}
                aria-current={current ? "date" : undefined}
                className={`planning-day planning-day-heading ${day.weekend ? "weekend" : ""} ${day.holiday ? "holiday" : ""} ${current ? "today" : ""}`}
              >
                <strong>
                  {day.dayName}
                  {day.holiday && (
                    <span className="holiday-name" title={day.holiday.name}>
                      {" · "}{day.holiday.name}
                    </span>
                  )}
                </strong>
                <span>
                  {period.view === "Monat"
                    ? new Date(`${day.datum}T00:00:00Z`).getUTCDate()
                    : dateCH(day.datum)}
                  {current && <b> · heute</b>}
                </span>
              </div>
            );
          })}
        </div>
        {rows.map((row) => (
          <div className="planning-calendar-row" key={row.employee.id}>
            <div className="planning-person" data-employee-color={employeeColor(row.employee.id).background} style={{ "--employee-accent": employeeColor(row.employee.id).background }}>
              <span className="planning-avatar" aria-hidden="true" style={{ backgroundColor: employeeColor(row.employee.id).background, color: employeeColor(row.employee.id).foreground }}>
                {initials(row.employee)}
              </span>
              <span className="planning-person-info">
                <strong>{row.employee.name}</strong>
                <span>
                  {row.total} h{" "}
                  {period.view === "Woche"
                    ? "diese Woche"
                    : "in diesem Zeitraum"}
                  {Number.isFinite(row.employee.pensumProzent)
                    ? ` · ${row.employee.pensumProzent}% Pensum`
                    : ""}
                </span>
              </span>
            </div>
            {row.days.map((day) => dayCell(day, row))}
          </div>
        ))}
        {!rows.length && (
          <div className="planning-calendar-empty">
            Keine Teammitglieder für diesen Zeitraum vorhanden.
          </div>
        )}
      </div>
      <PlanningMandateSidebar
        mandates={available}
        employees={employees}
        phases={phases}
        selectedMandate={selectedMandate}
        onSelect={setSelectedMandate}
      />
    </div>
  );
}

function PlanningYear({ period, employees, entries, mandates, phases, dispositions, onEdit }) {
  const weeks = fiscalWeeks(period),
    mandateById = Object.fromEntries(mandates.map((x) => [x.id, x])),
    phaseById = Object.fromEntries(phases.map((x) => [x.id, x])),
    dispositionById = Object.fromEntries(dispositions.map((x) => [x.id, x])),
    today = new Date().toISOString().slice(0, 10),
    currentWeek = weeks.find((week) => today >= week.from && today <= week.to),
    pastWeeks = weeks.filter((week) => week.to < today),
    [showPast, setShowPast] = useState(false),
    [expandedCells, setExpandedCells] = useState(() => new Set()),
    currentWeekRef = useRef(null),
    yearGridRef = useRef(null);
  useEffect(() => {
    if (!currentWeekRef.current) return;
    currentWeekRef.current.focus({ preventScroll: true });
    if (yearGridRef.current)
      yearGridRef.current.scrollTop = Math.max(0, currentWeekRef.current.offsetTop - 48);
  }, [currentWeek?.from, showPast]);
  const toggleCell = (key) => setExpandedCells((current) => {
    const next = new Set(current);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  return (
    <div
      className="planning-year-wrap"
      role="region"
      aria-label={`Jahresansicht Planungskalender für ${planningPeriodLabel(period)}`}
      tabIndex="0"
    >
      <div className="planning-year-controls">
        <Button
          variant="secondary"
          aria-expanded={showPast}
          aria-controls="planning-year-weeks"
          onClick={() => setShowPast((visible) => !visible)}
        >
          Vergangene Wochen {showPast ? "ausblenden" : "anzeigen"}
        </Button>
        <small>{pastWeeks.length} vergangene Kalenderwochen {showPast ? "sichtbar" : "kompakt zusammengefasst"}</small>
      </div>
      <p className="planning-year-hint">Projektfarben pro ISO-KW · {planningPeriodLabel(period)} (Okt.–Sept.)</p>
      <div
        id="planning-year-weeks"
        className="planning-year-grid"
        ref={yearGridRef}
        style={{ "--year-employees": employees.length }}
      >
        <div className="planning-year-head">Kalenderwoche</div>
        {employees.map((x) => (
          <div className="planning-year-head planning-year-employee-head" key={x.id} data-employee-color={employeeColor(x.id).background} style={{ backgroundColor: employeeColor(x.id).background, color: employeeColor(x.id).foreground }}>
            <span className="planning-avatar" aria-hidden="true" style={{ backgroundColor: employeeColor(x.id).foreground, color: employeeColor(x.id).background }}>{x.kuerzel || x.name?.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase() || "?"}</span>
            <span>{x.name}</span>
          </div>
        ))}
        {weeks.map((week) => {
          const past = week.to < today;
          if (past && !showPast) return null;
          return (
          <Fragment key={week.from}>
            <div
              ref={week === currentWeek ? currentWeekRef : undefined}
              tabIndex={week === currentWeek ? -1 : undefined}
              aria-current={week === currentWeek ? "date" : undefined}
              className={`planning-year-week ${week.partial ? "partial" : ""} ${week === currentWeek ? "current" : ""}`}
            >
              <strong>{week.label}</strong>
              <span>{week.isoYear}</span>
              <small>
                {dateCH(week.from)}–{dateCH(week.to)}
              </small>
            </div>
            {employees.map((employee) => {
              const items = entries.filter(
                (x) =>
                  x.mitarbeiterId === employee.id &&
                  x.datum >= week.from &&
                  x.datum <= week.to,
              );
              const cellKey = `${week.from}-${employee.id}`,
                expanded = expandedCells.has(cellKey),
                visibleItems = expanded ? items : items.slice(0, 3);
              return (
                <div className="planning-year-cell" key={employee.id} style={{ "--employee-accent": employeeColor(employee.id).background }}>
                  {visibleItems.map((entry) => (
                    <button
                      key={entry.id}
                      className="planning-year-chip"
                      data-project-color={projectColor(entry.mandatId).background}
                      style={{ backgroundColor: projectColor(entry.mandatId).background, color: projectColor(entry.mandatId).foreground }}
                      onClick={() => onEdit(entry)}
                    >
                      <strong>
                        {mandateById[entry.mandatId]?.name ||
                          "Nicht verfügbares Projekt"}
                      </strong>
                      <span>
                        {entry.stunden} h · {dateCH(entry.datum)}
                      </span>
                      {expanded && <span className="planning-year-details">
                        Phase: {phaseById[entry.phaseId]?.name || "Nicht angegeben"} · Disposition: {dispositionById[entry.dispositionsstatusId]?.name || "Nicht angegeben"} · Bearbeiten
                      </span>}
                    </button>
                  ))}
                  {items.length > 3 && (
                    <button
                      type="button"
                      className="planning-year-more"
                      aria-expanded={expanded}
                      onClick={() => toggleCell(cellKey)}
                    >
                      {expanded ? "Weniger anzeigen" : `+${items.length - 3} weitere`}
                    </button>
                  )}
                </div>
              );
            })}
          </Fragment>
          );
        })}
      </div>
    </div>
  );
}
function PlanningView({ route, navigate, user }) {
  const params = new URLSearchParams(route.search),
    filters = planningFilters(route.search),
    anchor =
      params.get("datum") ||
      params.get("von") ||
      new Date().toISOString().slice(0, 10),
    view = ["Tag", "Woche", "Monat", "Jahr"].includes(params.get("ansicht"))
      ? params.get("ansicht")
      : "Woche",
    period = planningPeriod(view, anchor),
    entriesPeriod = view === "Monat"
      ? { ...period, from: `${fiscalYearStartFor(anchor)}-10-01` }
      : period;
  const entries = useData(planningEntriesPath(entriesPeriod, filters)),
    employees = useData("/employees?limit=1000&includeInactive=true"),
    mandates = useData("/mandates?limit=1000&includeArchived=true"),
    phases = useData("/reference-data/phasen?limit=100"),
    dispositions = useData("/reference-data/dispositionsstatus?limit=100"),
    absences = useData("/absences?limit=1000"),
    holidays = useData(`/holidays?year=${period.from.slice(0, 4)}`),
    holidaysEnd = useData(`/holidays?year=${period.to.slice(0, 4)}`),
    [record, setRecord] = useState(null),
    [pendingMove, setPendingMove] = useState(null),
    [moveState, setMoveState] = useState({ status: "idle", message: "" });
  const fiscalStart = fiscalYearStartFor(anchor),
    currentWeek = isoWeekFor(anchor),
    [jumpYear, setJumpYear] = useState(fiscalStart),
    [jumpWeekYear, setJumpWeekYear] = useState(currentWeek.year),
    [jumpWeek, setJumpWeek] = useState(currentWeek.week),
    [jumpMonth, setJumpMonth] = useState(Number(anchor.slice(5, 7))),
    [jumpCalendarYear, setJumpCalendarYear] = useState(Number(anchor.slice(0, 4))),
    fiscalYears = useMemo(() => Array.from({ length: 5 }, (_, index) => fiscalStart + index), [fiscalStart]);
  useEffect(() => {
    setJumpYear(fiscalStart);
    setJumpWeekYear(currentWeek.year);
    setJumpWeek(currentWeek.week);
    setJumpMonth(Number(anchor.slice(5, 7)));
    setJumpCalendarYear(Number(anchor.slice(0, 4)));
  }, [anchor, fiscalStart, currentWeek.year, currentWeek.week]);
  const employeeItems = useMemo(
      () => employees.data?.items || [],
      [employees.data],
    ),
    mandateItems = useMemo(() => mandates.data?.items || [], [mandates.data]),
    dispositionItems = useMemo(
      () => dispositions.data?.items || [],
      [dispositions.data],
    );
  const update = (changes, temporal = {}) => {
    const search = planningFilterSearch(route.search, changes),
      query = new URLSearchParams(search);
    Object.entries(temporal).forEach(([key, value]) => query.set(key, value));
    navigate(`/planung?${query}`);
  };
  const go = (nextView, nextAnchor = anchor) =>
    update({}, { ansicht: nextView, datum: nextAnchor });
  const visibleEmployees = filters.mitarbeiterId
      ? employeeItems.filter((x) => x.id === filters.mitarbeiterId)
      : employeeItems.filter((x) => x.aktiv !== false),
    rows = entries.data?.items || [],
    rangeContext = {
      employeeId: filters.mitarbeiterId,
      from: entriesPeriod.from,
      to: period.to,
    },
    visibleAbsences = (absences.data?.items || []).filter((row) =>
      absenceInContext(row, rangeContext),
    ),
    visibleHolidays = [
      ...(holidays.data || []),
      ...(period.to.slice(0, 4) === period.from.slice(0, 4)
        ? []
        : holidaysEnd.data || []),
    ];
  const subtitle =
    period.view === "Woche"
      ? "Wochenansicht · Drag & Drop aus Mandatsliste"
      : period.view === "Monat"
        ? "Monatsansicht · welches Projekt an welchem Tag geplant ist"
        : period.view === "Jahr"
          ? "Jahresansicht · welches Projekt in welcher Kalenderwoche geplant ist"
          : "Tagesansicht · Planung pro Teammitglied";
  const previousLabel =
      period.view === "Monat"
        ? "← Vorheriger Monat"
        : period.view === "Jahr"
          ? "← Vorheriges Geschäftsjahr"
          : "← Vorherige Woche",
    nextLabel =
      period.view === "Monat"
        ? "Nächster Monat →"
        : period.view === "Jahr"
          ? "Nächstes Geschäftsjahr →"
          : "Nächste Woche →";
  const performMove = async (entry, mitarbeiterId, datum) => {
    if (entry.mitarbeiterId === mitarbeiterId && entry.datum === datum) return;
    setMoveState({ status: "saving", message: "" });
    try {
      await api(`/planning-entries/${entry.id}`, {
        method: "PATCH",
        body: JSON.stringify(planningMoveBody(entry, mitarbeiterId, datum)),
        successMessage: "Planungseintrag verschoben.",
      });
      entries.reload();
      setMoveState({ status: "success", message: "Planungseintrag wurde verschoben." });
    } catch (error) {
      const message = error.status === 409
        ? "Versionskonflikt: Der Planungseintrag wurde inzwischen geändert. Laden Sie die Planung neu und versuchen Sie es erneut."
        : error.status === 403
          ? "Keine Berechtigung: Sie dürfen diesen Planungseintrag nicht der gewählten Person oder dem gewählten Datum zuordnen."
          : `Verschieben fehlgeschlagen: ${error.message}`;
      setMoveState({ status: "error", message });
    }
  };
  const moveEntry = (entry, mitarbeiterId, datum) => {
    if (entry.mitarbeiterId !== mitarbeiterId) {
      setPendingMove({ entry, mitarbeiterId, datum });
      return;
    }
    performMove(entry, mitarbeiterId, datum);
  };
  const periodNavigation = <div className="planning-period-toolbar" aria-label="Periodennavigation">
    <Button variant="secondary" aria-label={period.view === "Jahr" ? `${previousLabel}: ${fiscalYearLabel(fiscalStart - 1)}` : undefined} onClick={() => go(period.view, movePlanningPeriod(period, -1))}>{previousLabel}</Button>
    <Button variant="secondary" aria-label={period.view === "Jahr" ? `${nextLabel}: ${fiscalYearLabel(fiscalStart + 1)}` : undefined} onClick={() => go(period.view, movePlanningPeriod(period, 1))}>{nextLabel}</Button>
    <Badge>{planningPeriodLabel(period)}</Badge>
    <Button variant="secondary" onClick={() => go(period.view, new Date().toISOString().slice(0, 10))}>Heute</Button>
    <span className="planning-period-actions"><Button onClick={() => setRecord({ mitarbeiterId: filters.mitarbeiterId || "", datum: anchor })}>+ Neuer Planungseintrag</Button></span>
  </div>;
  const jumpNavigation = period.view === "Woche" ? <form className="planning-jump-toolbar" aria-label="Zu Kalenderjahr und Kalenderwoche springen" onSubmit={event => {
    event.preventDefault();
    const datum = isoWeekDate(jumpWeekYear, jumpWeek);
    if (datum) update({}, { ansicht: "Woche", datum });
  }}>
    <strong>Springe zu: Kalenderjahr + Kalenderwoche</strong>
    <label>Jahr<select value={jumpWeekYear} onChange={event => {
      const year = Number(event.target.value);
      setJumpWeekYear(year);
      setJumpWeek(week => Math.min(week, isoWeeksInYear(year)));
    }}>{fiscalYears.map(year => <option key={year} value={year}>{year}</option>)}</select></label>
    <label>Kalenderwoche<select value={jumpWeek} onChange={event => setJumpWeek(Number(event.target.value))}>{Array.from({ length: isoWeeksInYear(jumpWeekYear) }, (_, index) => {
      const week = index + 1;
      return <option key={week} value={week}>{planningPeriodLabel(planningPeriod("Woche", isoWeekDate(jumpWeekYear, week)))}</option>;
    })}</select></label>
    <Button type="submit" variant="secondary">Springen</Button>
  </form> : period.view === "Monat" ? <form className="planning-jump-toolbar" aria-label="Zu Monat und Jahr springen" onSubmit={event => {
    event.preventDefault();
    const datum = monthDate(jumpCalendarYear, jumpMonth);
    if (datum) update({}, { ansicht: "Monat", datum });
  }}>
    <strong>Springe zu: Monat + Jahr</strong>
    <label>Monat<select value={jumpMonth} onChange={event => setJumpMonth(Number(event.target.value))}>{Array.from({ length: 12 }, (_, index) => <option key={index + 1} value={index + 1}>{new Intl.DateTimeFormat("de-CH", { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2020, index, 1)))}</option>)}</select></label>
    <label>Jahr<input type="number" min="1900" max="2200" value={jumpCalendarYear} onChange={event => setJumpCalendarYear(event.target.value)} /></label>
    <Button type="submit" variant="secondary">Springen</Button>
  </form> : <form className="planning-jump-toolbar" aria-label="Zu Geschäftsjahr springen" onSubmit={event => {
    event.preventDefault();
    update({}, { ansicht: "Jahr", datum: `${jumpYear}-10-01` });
  }}>
    <strong>Springe zu: Geschäftsjahr</strong>
    <label>Geschäftsjahr<select value={jumpYear} onChange={event => setJumpYear(Number(event.target.value))}>{fiscalYears.map(year => <option key={year} value={year}>{fiscalYearLabel(year)}</option>)}</select></label>
    <Button type="submit" variant="secondary">Springen</Button>
  </form>;
  const colorLegend = <p className={`planning-color-legend ${period.view === "Jahr" ? "planning-color-legend-year" : ""}`} aria-label={period.view === "Monat" ? "Monatslegende" : undefined}><span><i className="employee-dot" /> Mitarbeiterfarbe</span><span>{period.view === "Jahr" ? "Projektfarbe" : "Hintergrund und Rand kennzeichnen die Disposition."}</span></p>;
  const calendar = <LoadState state={entries} keepPreviousData>
    {period.view === "Jahr" ? <PlanningYear period={period} employees={visibleEmployees} entries={rows} mandates={mandateItems} phases={phases.data?.items || []} dispositions={dispositionItems} onEdit={setRecord} /> : <PlanningCalendar period={period} rangePeriod={entriesPeriod} employees={visibleEmployees} entries={rows} absences={visibleAbsences} holidays={visibleHolidays} employeeId={filters.mitarbeiterId} mandates={mandateItems} dispositions={dispositionItems} phases={phases.data?.items || []} onCreate={(mitarbeiterId, datum, mandatId) => setRecord({ mitarbeiterId, datum, mandatId: mandatId || filters.mandatId || "" })} onEdit={setRecord} onMove={moveEntry} />}
  </LoadState>;
  return (
    <>
      <PageHeader title="Planung" meta={subtitle} />
      <div className="planning-view-toolbar">
        <Tabs
          label="Planungsansicht"
          value={period.view}
          onChange={(next) => go(next)}
          items={["Woche", "Monat", "Jahr"].map((x) => ({
            value: x,
            label: x,
          }))}
        />
      </div>
      {period.view !== "Monat" && <>{periodNavigation}{jumpNavigation}{colorLegend}</>}
      {moveState.status === "saving" && <Notice>Planungseintrag wird verschoben …</Notice>}
      {moveState.status === "success" && <Notice>{moveState.message}</Notice>}
      {moveState.status === "error" && (
        <Notice tone="error">
          {moveState.message}{" "}
          <Button
            variant="secondary"
            onClick={() => setMoveState({ status: "idle", message: "" })}
          >
            Schliessen
          </Button>
        </Notice>
      )}
      {period.view === "Monat" ? <section className="planning-month-scroll" aria-label="Monatsplanung" aria-busy={entries.status === "loading"} tabIndex="0">
        <header className="planning-month-header">{periodNavigation}{jumpNavigation}{colorLegend}</header>
        {calendar}
      </section> : calendar}
      {record && (
        <PlanningDialog
          record={record}
          employees={employeeItems.filter((x) => x.aktiv !== false)}
          mandates={mandateItems.filter((x) => x.status !== "archiviert")}
          onClose={() => setRecord(null)}
          onSaved={() => {
            setRecord(null);
            entries.reload();
          }}
        />
      )}
      {pendingMove && (
        <Dialog title="Zuweisung bestätigen" onClose={() => setPendingMove(null)}>
          <p>
            Möchten Sie <strong>{mandateItems.find((item) => item.id === pendingMove.entry.mandatId)?.name || "diesen Planungseintrag"}</strong>{" "}
            wirklich von <strong>{employeeItems.find((item) => item.id === pendingMove.entry.mitarbeiterId)?.name || "der bisherigen Person"}</strong>{" "}
            zu <strong>{employeeItems.find((item) => item.id === pendingMove.mitarbeiterId)?.name || "der gewählten Person"}</strong> verschieben?
          </p>
          <div className="dialog-actions">
            <Button variant="secondary" onClick={() => setPendingMove(null)}>Abbrechen</Button>
            <Button onClick={() => {
              const move = pendingMove;
              setPendingMove(null);
              performMove(move.entry, move.mitarbeiterId, move.datum);
            }}>Verschieben bestätigen</Button>
          </div>
        </Dialog>
      )}
    </>
  );
}
export const planningEmployeeColor = (employeeId = "") => {
  const palette = ["#5b7c99", "#7ba393", "#b78b5d", "#8a72a5", "#bf6f61", "#668f8c", "#9a805b", "#6f789f"];
  return palette[mandateDotIndex(employeeId)];
};

export function ProjectDeadlinePanel({ mandate }) {
  const milestones = useData(mandate?.id ? "/milestones?limit=100" : null);
  if (!mandate || mandate.intern === true || mandate.typ === "Abwesenheit")
    return <p className="planning-deadline-empty">Kein Zeitplan für Abwesenheiten/interne Tätigkeiten.</p>;
  const rows = (milestones.data?.items || [])
    .filter((item) => item.mandatId === mandate.id)
    .sort((a, b) => String(a.datum).localeCompare(String(b.datum)));
  return <div className="planning-deadline-panel">
    {milestones.status === "loading" && <small>Zeitplan wird geladen …</small>}
    {milestones.status === "error" && <Notice tone="error">Zeitplan konnte nicht geladen werden.</Notice>}
    {milestones.status === "success" && (rows.length ? <ul>{rows.map((row) => <li key={row.id}><time dateTime={row.datum}>{dateCH(row.datum)}</time><span>{row.bezeichnung}</span>{row.kritisch && <Badge tone="red">kritisch</Badge>}</li>)}</ul> : <p>Noch keine Zeitplan-Einträge.</p>)}
  </div>;
}

export function PlanningDialog({ record, employees, mandates, onClose, onSaved }) {
  const phases = useData("/reference-data/phasen?limit=100"),
    dispositions = useData("/reference-data/dispositionsstatus?limit=100"),
    editing = Boolean(record.id);
  const [form, setForm] = useState({
      mitarbeiterIds: [record.mitarbeiterId || employees[0]?.id || ""].filter(Boolean),
      mandatId: record.mandatId || mandates[0]?.id || "",
      von: record.datum || "", bis: record.datum || "", stunden: record.stunden || 8,
      halbtag: record.halbtag || "", phaseId: record.phaseId || "",
      dispositionsstatusId: record.dispositionsstatusId || "",
      inhouseExtern: record.inhouseExtern || "Inhouse", kommentar: record.kommentar || "", nurArbeitstage: false,
    }),
    [internalProject, setInternalProject] = useState(false),
    [error, setError] = useState(""), [saving, setSaving] = useState(false),
    [forbidden, setForbidden] = useState(false),
    [confirmDelete, setConfirmDelete] = useState(false), [conflict, setConflict] = useState(false),
    [deadlinesOpen, setDeadlinesOpen] = useState(false),
    idempotencyKey = useRef(`planung-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`}`);
  const inaccessibleEmployee = editing && !employees.some((item) => item.id === record.mitarbeiterId);
  const inaccessibleMandate = editing && !mandates.some((item) => item.id === record.mandatId);
  const initialForm = useRef(form), dirty = JSON.stringify(form) !== JSON.stringify(initialForm.current), requestClose = useUnsavedChanges(dirty, onClose);
  const phaseItems = (phases.data?.items || []).filter((item) => item.aktiv !== false);
  const selectedMandate = mandates.find((item) => item.id === form.mandatId) || (inaccessibleMandate ? record : null);
  const phaseOptions = phaseItems.map((item) => ({
    value: item.id,
    label: item.code && item.name && item.code !== item.name
      ? `${item.code} — ${item.name}`
      : item.code || item.name || item.id,
  }));
  const dispositionOptions = (dispositions.data?.items || [])
    .filter((item) => item.aktiv !== false && ["flex", "fx-bü", "fx-ext", "nfx", "res"].includes(item.code))
    .filter((item, index, items) => items.findIndex((candidate) => candidate.code === item.code) === index)
    .map((item) => ({ value: item.id, code: item.code, label: dispositionPresentation(item.code) }));
  const planningDate = form.von || record.datum || new Date().toISOString().slice(0, 10);
  const { from: horizonMin, to: horizonMax } = fiscalYearFor(planningDate);

  useEffect(() => {
    setForm((current) => {
      const next = { ...current, dispositionsstatusId: current.dispositionsstatusId || dispositionOptions.find((item) => item.code === "nfx")?.value || dispositionOptions[0]?.value || "" };
      if (JSON.stringify(current) === JSON.stringify(initialForm.current)) initialForm.current = next;
      return next;
    });
  }, [dispositions.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeMandate = (mandatId) => { setDeadlinesOpen(false); setForm((current) => ({ ...current, mandatId })); };
  const select = (key, label, options, required = true) => <Field id={`plan-${key}`} label={label}><select required={required} value={form[key]} onChange={(e) => { const value = e.target.value; setForm((current) => ({ ...current, [key]: value })); }}>{!options.some((x) => typeof x === "object" && x.value === "") && <option value="">Bitte wählen</option>}{options.map((x) => <option key={x.value ?? x} value={x.value ?? x}>{x.label ?? x}{key === "stunden" ? "h" : ""}</option>)}</select></Field>;
  const toggleEmployee = (id) => setForm({ ...form, mitarbeiterIds: form.mitarbeiterIds.includes(id) ? form.mitarbeiterIds.filter((x) => x !== id) : [...form.mitarbeiterIds, id] });
  const submit = async (event) => {
    event.preventDefault();
    const endDate = editing ? form.von : form.bis;
    if (!form.mitarbeiterIds.length || !form.mandatId || !form.von || !endDate) { setError("Bitte alle Pflichtfelder ausfüllen."); return; }
    if (endDate < form.von) { setError("Das Enddatum darf nicht vor dem Startdatum liegen."); return; }
    if (form.von < horizonMin || form.von > horizonMax || endDate < horizonMin || endDate > horizonMax) { setError(`Start- und Enddatum müssen im Geschäftsjahr ${dateCH(horizonMin)}–${dateCH(horizonMax)} liegen.`); return; }
    setSaving(true); setError(""); setConflict(false); setForbidden(false);
    try {
      const common = { mandatId: form.mandatId, stunden: Number(form.stunden), phaseId: form.phaseId || null, dispositionsstatusId: form.dispositionsstatusId, inhouseExtern: form.inhouseExtern, kommentar: form.kommentar.trim() || null, ...(Number(form.stunden) === 4 && form.halbtag ? { halbtag: form.halbtag } : {}) };
      const body = editing ? { ...common, mitarbeiterId: form.mitarbeiterIds[0], datum: form.von, version: record.version } : { ...common, mitarbeiterIds: form.mitarbeiterIds, von: form.von, bis: form.bis, nurArbeitstage: form.nurArbeitstage };
      await api(editing ? `/planning-entries/${record.id}` : "/planning-entry-series", { method: editing ? "PATCH" : "POST", ...(editing ? {} : { headers: { "Idempotency-Key": idempotencyKey.current }, retry: 1 }), body: JSON.stringify(body), successMessage: "Planungseintrag gespeichert." }); onSaved();
    } catch (x) { setConflict(x.status === 409); setForbidden(x.status === 403); setError(x.message); } finally { setSaving(false); }
  };
  const remove = async () => { setSaving(true); setError(""); try { await api(`/planning-entries/${record.id}?version=${record.version}`, { method: "DELETE", successMessage: "Planungseintrag gelöscht." }); onSaved(); } catch (x) { setConflict(x.status === 409); setError(x.message); setConfirmDelete(false); } finally { setSaving(false); } };
  const exportCalendar = () => downloadPlanningEntryIcs({ entry: record, employeeName: employees.find((x) => x.id === record.mitarbeiterId)?.name || record.mitarbeiterId, mandateName: mandates.find((x) => x.id === record.mandatId)?.name || record.mandatId });
  const internalMandates = mandates.filter((item) => item.status !== "archiviert" && item.intern === true);
  const mandateOptions = [...(inaccessibleMandate ? [{ id: record.mandatId, name: `${record.mandatName || record.mandatId} (nicht zugänglich)`, status: "inaccessible" }] : []), ...mandates].filter((x) => x.status !== "archiviert").map((x) => ({ value: x.id, label: x.name }));
  const projectField = <Field id="plan-mandatId" label="Projekt"><select required value={form.mandatId} onChange={(e) => changeMandate(e.target.value)}><option value="">Bitte wählen</option>{mandateOptions.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}</select></Field>;
  const internalProjectField = <Field id="plan-internal-mandatId" label="Interne Tätigkeit"><select id="plan-internal-mandatId" required value={form.mandatId} onChange={(e) => changeMandate(e.target.value)}><option value="">Bitte wählen</option>{internalMandates.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>{!internalMandates.length && <small className="planning-internal-empty">Keine aktiven internen Projekte verfügbar.</small>}</Field>;
  const toggleInternalProject = (checked) => {
    setInternalProject(checked);
    const currentIsInternal = internalMandates.some((item) => item.id === form.mandatId);
    if (checked && !currentIsInternal) changeMandate(internalMandates[0]?.id || "");
  };
  const employeeField = <Field id="plan-employees" label={editing ? "Teammitglied" : "Teammitglieder"}><div className="planning-employee-panel">{inaccessibleEmployee && form.mitarbeiterIds.includes(record.mitarbeiterId) && <label className="checkbox-line"><input type="radio" name="planning-employee" checked disabled readOnly />{record.mitarbeiterName || record.mitarbeiterId} (nicht zugänglich)</label>}{employees.map((x) => <label key={x.id} className="checkbox-line"><input type={editing ? "radio" : "checkbox"} name={editing ? "planning-employee" : `planning-employee-${x.id}`} aria-label={x.name} checked={form.mitarbeiterIds.includes(x.id)} onChange={() => editing ? setForm({ ...form, mitarbeiterIds: [x.id] }) : toggleEmployee(x.id)} /><span className="planning-employee-dot" style={{ "--employee-color": planningEmployeeColor(x.id) }} aria-hidden="true"/><span>{x.name}</span><small>({x.kuerzel || x.id})</small></label>)}</div></Field>;
  const statusField = select("dispositionsstatusId", "Status Termin", dispositionOptions);
  const commentField = <Field id="plan-kommentar" label="Kommentar (optional)"><textarea placeholder="Gilt für alle damit erstellten Einträge …" value={form.kommentar} onChange={(e) => setForm({ ...form, kommentar: e.target.value })}/></Field>;
  return <Dialog title={editing ? "Planungseintrag bearbeiten" : "Neuer Planungseintrag"} onClose={requestClose}>
    {!editing && <p className="dialog-subtitle">Ein Eintrag kann über mehrere Tage und mehrere Teammitglieder gleichzeitig gelten.</p>}
    <form className={`domain-form planning-entry-form${editing ? " planning-entry-form-edit" : ""}`} onSubmit={submit}>
      {!editing && <label className="checkbox-line planning-internal-toggle"><input type="checkbox" checked={internalProject} onChange={(event) => toggleInternalProject(event.target.checked)}/><span>Internes Projekt</span></label>}
      {!editing && internalProject ? internalProjectField : projectField}
      <Field id="plan-phaseId" label="Phase"><select value={form.phaseId} onChange={(event) => { const phaseId = event.target.value; setForm((current) => ({ ...current, phaseId })); }}><option value="">– keine Phase –</option>{phaseOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></Field>
      {editing ? <Field id="plan-von" label="Datum"><input type="date" required min={horizonMin} max={horizonMax} value={form.von} onChange={(e) => setForm({ ...form, von: e.target.value, bis: e.target.value })}/></Field> : <><Field id="plan-zeitraum" label="Zeitraum"><div className="planning-date-range"><input aria-label="Zeitraum Start" type="date" required min={horizonMin} max={horizonMax} value={form.von} onChange={(e) => setForm({ ...form, von: e.target.value })}/><span className="planning-date-separator">bis</span><input aria-label="Zeitraum Ende" type="date" required min={horizonMin} max={horizonMax} value={form.bis} onChange={(e) => setForm({ ...form, bis: e.target.value })}/></div><small className="planning-horizon">Geschäftsjahr: {dateCH(horizonMin)}–{dateCH(horizonMax)}</small></Field><Field id="plan-erzeugungstage" label="Erzeugungstage"><label className="checkbox-line"><input type="radio" name="planning-days" checked={!form.nurArbeitstage} onChange={() => setForm({ ...form, nurArbeitstage: false })}/>Alle Kalendertage (einschliesslich Wochenenden und Feiertagen)</label><label className="checkbox-line"><input type="radio" name="planning-days" checked={form.nurArbeitstage} onChange={() => setForm({ ...form, nurArbeitstage: true })}/>Nur Arbeitstage (Montag bis Freitag)</label></Field></>}
      {employeeField}
      <div className="planning-paired-fields">{select("stunden", "Stunden / Tag", [8, 4, 2, 1])}{select("inhouseExtern", "Inhouse / Extern", [{ value: "Inhouse", label: "Inhouse (bü)" }, { value: "Extern", label: "Extern (ext)" }])}</div>
      {Number(form.stunden) === 4 && select("halbtag", "Vormittag oder Nachmittag (optional)", [{ value: "", label: "– nicht festgelegt –" }, { value: "Vormittag", label: "Vormittag (08:00–12:00)" }, { value: "Nachmittag", label: "Nachmittag (13:00–17:00)" }], false)}
      {statusField}{commentField}
      {!editing && <div className="planning-deadlines"><button className="planning-deadline-toggle" type="button" aria-expanded={deadlinesOpen} onClick={() => setDeadlinesOpen((open) => !open)}>{deadlinesOpen ? "Zeitplan (Deadlines) ausblenden ▴" : "Zeitplan (Deadlines) anzeigen ▾"}</button>{deadlinesOpen && <ProjectDeadlinePanel key={form.mandatId} mandate={selectedMandate}/>}</div>}
      {(inaccessibleEmployee || inaccessibleMandate) && <Notice tone="error">Mindestens eine gespeicherte Referenz ist nicht mehr zugänglich. Der Eintrag kann gelöscht oder nach Auswahl gültiger Referenzen gespeichert werden.</Notice>}
      {error && <Notice tone="error">{conflict ? <><strong>Versionskonflikt.</strong> Der Eintrag wurde inzwischen geändert. Neu laden und erneut versuchen. </> : forbidden ? <><strong>Keine Berechtigung.</strong> Sie dürfen den Eintrag nicht der gewählten Person zuordnen. </> : "Aktion fehlgeschlagen: "}{error} {conflict ? <Button type="button" variant="secondary" onClick={onSaved}>Neu laden</Button> : !forbidden && <Button type="button" variant="secondary" onClick={submit}>Erneut versuchen</Button>}</Notice>}
      <div className="form-actions planning-entry-actions">
        {editing && <Button type="button" variant="secondary" onClick={exportCalendar}>Als .ics exportieren</Button>}
        {editing && !confirmDelete && <Button type="button" variant="secondary" onClick={() => setConfirmDelete(true)}>Löschen</Button>}
        {confirmDelete && <><span className="delete-question">Planungseintrag wirklich löschen?</span><Button type="button" variant="secondary" onClick={() => setConfirmDelete(false)}>Nein</Button><Button type="button" onClick={remove} disabled={saving}>Ja, löschen</Button></>}
        {!editing && <Button disabled={saving || !form.mitarbeiterIds.length || !form.dispositionsstatusId}>{saving ? "Wird gespeichert …" : "Eintrag speichern"}</Button>}
        <Button type="button" variant="secondary" onClick={requestClose}>Abbrechen</Button>
        {editing && <Button disabled={saving || !form.mitarbeiterIds.length || !form.dispositionsstatusId}>{saving ? "Wird gespeichert …" : "Speichern"}</Button>}
      </div>
    </form>
  </Dialog>;
}
export function PlanningYearRollDialog({ onClose }) {
  const year = new Date().getUTCFullYear(),
    [form, setForm] = useState({ ausgangsjahr: year, zieljahr: year + 1 }),
    [state, setState] = useState({ status: "idle" }),
    [confirmed, setConfirmed] = useState(false),
    [history, setHistory] = useState({ status: "loading", items: [] });
  const idempotencyKey = useRef(
    `roll-${year}-${year + 1}-${window.crypto?.randomUUID?.() || Date.now()}`,
  );
  const initialForm = useRef(form),
    completed = ["completed", "historical"].includes(state.status),
    dirty =
      !completed &&
      (JSON.stringify(form) !== JSON.stringify(initialForm.current) ||
        state.status !== "idle" ||
        confirmed),
    requestClose = useUnsavedChanges(dirty, onClose);
  const loadHistory = useCallback(() => {
    setHistory((h) => ({ ...h, status: "loading" }));
    api("/planning-years/history?limit=10")
      .then((data) =>
        setHistory({ status: "success", items: data.items || [] }),
      )
      .catch((error) => setHistory({ status: "error", error, items: [] }));
  }, []);
  useEffect(loadHistory, [loadHistory]);
  const change = (key, value) => {
    const next = { ...form, [key]: Number(value) };
    if (key === "ausgangsjahr") next.zieljahr = Number(value) + 1;
    setForm(next);
    setConfirmed(false);
    setState({ status: "idle" });
    idempotencyKey.current = `roll-${next.ausgangsjahr}-${next.zieljahr}-${window.crypto?.randomUUID?.() || Date.now()}`;
  };
  const preview = async () => {
    setState({ status: "loading" });
    setConfirmed(false);
    try {
      const data = await api("/planning-years/preview", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setState({ status: data.anzahlGerollt ? "preview" : "empty", data });
    } catch (error) {
      setState({
        status:
          error.status === 403
            ? "forbidden"
            : error.status === 409
              ? "conflict"
              : "validation",
        error,
      });
    }
  };
  const execute = async () => {
    setState((s) => ({ ...s, status: "saving" }));
    try {
      const data = await api("/planning-years/roll", {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey.current },
        retry: 1,
        body: JSON.stringify({
          ...form,
          confirmationToken: state.data.confirmationToken,
        }),
        successMessage: "Planungsjahr erfolgreich gerollt.",
      });
      setState({ status: data.historical ? "historical" : "completed", data });
      loadHistory();
    } catch (error) {
      setState((s) => ({
        ...s,
        status:
          error.status === 403
            ? "forbidden"
            : error.status === 409
              ? "conflict"
              : "failure",
        error,
      }));
    }
  };
  const data = state.data;
  return (
    <Dialog title="Planungsjahr rollen" onClose={requestClose}>
      <div className="domain-form">
        <p>
          Planung nach gleicher ISO-Kalenderwoche und gleichem Wochentag
          übertragen. Fixierte Dispositionen werden auf nfx zurückgesetzt.
        </p>
        <div className="grid grid-2">
          <Field id="roll-source" label="Ausgangsjahr">
            <input
              min="1583"
              max="9998"
              type="number"
              value={form.ausgangsjahr}
              onChange={(e) => change("ausgangsjahr", e.target.value)}
            />
          </Field>
          <Field id="roll-target" label="Zieljahr">
            <input
              type="number"
              value={form.zieljahr}
              onChange={(e) => change("zieljahr", e.target.value)}
            />
          </Field>
        </div>
        {state.status === "loading" && <Notice>Vorschau wird geladen …</Notice>}
        {state.status === "saving" && (
          <Notice>Planungsjahr wird transaktional gespeichert …</Notice>
        )}
        {["preview", "empty"].includes(state.status) && (
          <Notice tone={state.status === "empty" ? "error" : undefined}>
            <strong>
              {fiscalYearLabel(data.ausgangsjahr)} → {fiscalYearLabel(data.zieljahr)}
            </strong>
            <br />
            {data.anzahlGerollt} Einträge werden gerollt ·{" "}
            {data.anzahlUebersprungen} übersprungen · {data.anzahlKonflikte}{" "}
            bereits gerollt/konfliktbehaftet.
            <br />
            Inaktive Mandate: {data.outcomes.inaktiveMandate} · ungültige KW53:{" "}
            {data.outcomes.ungueltigeKW53} · ausserhalb Zieljahr:{" "}
            {data.outcomes.ausserhalbZieljahr} · fx→nfx:{" "}
            {data.outcomes.dispositionZurueckgesetzt}
            {state.status === "empty" && (
              <>
                <br />
                <strong>Keine rollbaren Einträge vorhanden.</strong>
              </>
            )}
          </Notice>
        )}
        {["validation", "forbidden", "failure", "conflict"].includes(
          state.status,
        ) && (
          <Notice tone="error">
            <strong>
              {state.status === "forbidden"
                ? "Kein Zugriff"
                : state.status === "validation"
                  ? "Validierung fehlgeschlagen"
                  : state.status === "conflict"
                    ? "Konflikt"
                    : "Ausführung fehlgeschlagen"}
              .
            </strong>{" "}
            {state.error?.message}{" "}
            <Button
              variant="secondary"
              onClick={() => {
                preview();
              }}
            >
              Erneut versuchen
            </Button>
          </Notice>
        )}
        {["completed", "historical"].includes(state.status) && (
          <Notice>
            <strong>
              {state.status === "historical"
                ? "Bereits vorhandenes Ergebnis"
                : "Planungsjahr vollständig gerollt"}
              .
            </strong>{" "}
            {data.anzahlGerollt} Einträge · Protokoll {data.id}
          </Notice>
        )}
        {state.status === "preview" && (
          <label className="checkbox-line">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            Ich bin privilegiert und bestätige die irreversible Ausführung
            ausdrücklich.
          </label>
        )}
        <div className="form-actions">
          <Button variant="secondary" onClick={requestClose}>
            Abbrechen
          </Button>
          <Button
            variant="secondary"
            onClick={preview}
            disabled={["loading", "saving"].includes(state.status)}
          >
            Vorschau aktualisieren
          </Button>
          <Button
            onClick={execute}
            disabled={state.status !== "preview" || !confirmed}
          >
            Rollen bestätigen
          </Button>
        </div>
        <Card title="Protokoll">
          {history.status === "loading" ? (
            <p>Historie wird geladen …</p>
          ) : history.status === "error" ? (
            <p>
              Protokoll konnte nicht geladen werden.{" "}
              <Button variant="secondary" onClick={loadHistory}>
                Erneut versuchen
              </Button>
            </p>
          ) : history.items.length ? (
            history.items.map((item) => (
              <p key={item.id}>
                <strong>
                  {fiscalYearLabel(item.ausgangsjahr)} → {fiscalYearLabel(item.zieljahr)}
                </strong>{" "}
                · {item.anzahlGerollt} gerollt ·{" "}
                {new Date(item.createdAt).toLocaleString("de-CH")}
              </p>
            ))
          ) : (
            <p>Noch kein Rollvorgang protokolliert.</p>
          )}
        </Card>
      </div>
    </Dialog>
  );
}
export function CapacityView({ navigate }) {
  const params = new URLSearchParams(window.location.search);
  const initial = dashboardWeek(
    params.get("von") || new Date().toISOString().slice(0, 10),
  ).from;
  const weeksCount = [4, 8, 12].includes(Number(params.get("wochen")))
    ? Number(params.get("wochen"))
    : 8;
  const cap = useData(`/capacity?from=${initial}&weeks=${weeksCount}`),
    employees = cap.data || [],
    weeks = (employees[0]?.wochen || []).slice(0, weeksCount);
  const missing = (cap.data || []).filter(
    (item) =>
      !item.name || !item.kuerzel || !Number.isFinite(item.pensumProzent),
  );
  return (
    <>
      <PageHeader
        title="Kapazitätsübersicht"
        meta="Wochenstunden je Teammitglied, automatisch aus der Planung berechnet"
      />
      <div className="capacity-legend" aria-label="Kapazitätslegende">
        {capacityLegend.map((status) => (
          <div className="capacity-legend-item" key={status.key}>
            <span
              className={`capacity-legend-swatch ${status.className}`}
              aria-hidden="true"
            />
            <span>{status.label}</span>
          </div>
        ))}
      </div>
      <LoadState state={cap}>
        {missing.length > 0 && (
          <Notice tone="error">
            Unvollständige Mitarbeiterdaten bei {missing.length}{" "}
            Datensatz/Datensätzen. Berechnete Werte bleiben sichtbar; fehlende
            Stammdaten sind mit „–“ markiert.
          </Notice>
        )}
        <div
          className="capacity-heat-wrap"
          tabIndex="0"
          aria-label="Kapazitätstabelle, horizontal scrollbar"
        >
          <table className="capacity-heat">
            <thead>
              <tr>
                <th>Kalenderwoche</th>
                {employees.map((employee, index) => (
                  <th key={employee.mitarbeiterId}>
                    <button
                      type="button"
                      className={`capacity-employee capacity-employee-${index % 10}`}
                      title={employee.name || "–"}
                      aria-label={`${employee.name || employee.kuerzel || employee.mitarbeiterId} im Team öffnen`}
                      onClick={() =>
                        navigate(
                          `/team?mitarbeiterId=${encodeURIComponent(employee.mitarbeiterId)}`,
                        )
                      }
                    >
                      {employee.kuerzel || "–"}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, index) => (
                <tr key={week.von}>
                  <th className={index === 0 ? "is-today-simple" : undefined}>
                    KW {isoWeekNumber(week.von)}
                    {index === 0 && <b> · aktuell</b>}
                  </th>
                  {employees.map((employee) => {
                    const value = employee.wochen[index];
                    return (
                      <td key={employee.mitarbeiterId}>
                        <button
                          className={`capacity-cell ${capacityStatus(value.status).className}`}
                          title={`${employee.name || employee.kuerzel} · KW ${isoWeekNumber(value.von)} · ${value.status} (${value.auslastungProzent}% von ${value.verfuegbar}h verfügbar${value.abwesenheit ? `, ${value.abwesenheit}h Abwesenheit` : ""})`}
                          aria-label={`${employee.name || employee.kuerzel}, KW ${isoWeekNumber(value.von)}: ${value.status}; ${value.geplant} Stunden geplant. In Planung öffnen.`}
                          onClick={() =>
                            navigate(
                              `/planung?mitarbeiterId=${encodeURIComponent(employee.mitarbeiterId)}&von=${value.von}`,
                            )
                          }
                        >
                          <span aria-hidden="true">
                            {capacityStatus(value.status).symbol}{" "}
                          </span>
                          {value.geplant}h
                          <span className="sr-only">
                            {" "}
                            · {capacityStatus(value.status).label}
                          </span>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {!employees.length && (
            <p className="empty-state">
              Keine Kapazitätsdaten für diesen Zeitraum vorhanden.
            </p>
          )}
        </div>
      </LoadState>
    </>
  );
}
export function RouteView({ route, navigate, user }) {
  if (route.id === "planung")
    return <PlanningView route={route} navigate={navigate} user={user} />;
  if (route.id === "kapazitaet") return <CapacityView navigate={navigate} />;
  if (route.id === "projekte")
    return <ProjectPortfolio route={route} navigate={navigate} user={user} />;
  if (route.id === "bestaetigungen")
    return <ConfirmationView route={route} navigate={navigate} user={user} />;
  if (route.id === "team") return <TeamView route={route} user={user} />;
  return <ResourceView route={route} navigate={navigate} user={user} />;
}
export function ForbiddenView() {
  return (
    <Card>
      <Badge tone="red">Kein Zugriff</Badge>
      <h1>Zugriff nicht erlaubt</h1>
      <p>
        Sie verfügen nicht über die erforderliche Berechtigung für diese
        Ansicht.
      </p>
    </Card>
  );
}
